import { Button, Divider, FormControl, MenuItem, IconButton, Paper, Select, Typography, CircularProgress, Checkbox, ListItemText } from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import L from 'leaflet';
import { MapContainer, TileLayer, Polyline, Tooltip, CircleMarker, Polygon, SVGOverlay, LayersControl } from 'react-leaflet';
import { scaleSequential } from 'd3-scale';
import { interpolateTurbo } from 'd3-scale-chromatic';

import { selectRTFO, setCurrentStageName } from '../../reducers/rtfoSlice';
import { useDispatch, useSelector } from 'react-redux';
import { FastForward, FastRewind, PlayArrow, Satellite, SkipNext, SkipPrevious, Stop } from '@mui/icons-material';
import { useTheme } from '@mui/styles';
import { selectUser } from '../../reducers/userSlice';
import RTFOMapLegend from './RTFOMapLegend';
import { RTFOWellboreTrajectoryChart } from './RTFOWellboreTrajectoryChart';
import { getMetricParamName, getMetricUnits } from './RTFOMetricsPanel';
import { chartColors } from '../../helpers/chartColors';
import * as turf from '@turf/turf';
import ScrollZoomControl from './ScrollZoomControl';
import ScrollZoomControlOverlay from './ScrollZoomControlOverlay';
import { RTFOMapMeasureControl } from './RTFOMapMeasureControl';
import { colors } from '../../helpers/colors';
import RTFOMapDrawingControl from './RTFOMapDrawingControl';
import RTFOMapFeatures from './RTFOMapFeatures';

const { BaseLayer, Overlay } = LayersControl;

const RTFOMapWithWellbore = ({ selectedWellName='All', allPadsMetrics, isMetricsMap, reportDate=new Date() }) => {
  
  const theme = useTheme();

  const dispatch = useDispatch();

  const mapRef = useRef(null);
  const user = useSelector(selectUser);
  const rtfo = useSelector(selectRTFO);
  const currentRTFO = rtfo?.value;
  const currentStageName = currentRTFO?.currentStageName;
  const currentWellName = currentStageName?.split('_')?.[1]?.toLowerCase();
  const darkMode = user?.value?.darkMode;

  const padHasSeismicityData = (currentRTFO?.seismicData?.realtime || currentRTFO?.seismicData?.highPrecision) ? true : false;
  // const isHighPrecisionSeismicityData = currentRTFO?.seismicData?.highPrecision || false;

  const filteredAllPadsMetrics = useMemo(() => {
    if (!allPadsMetrics) return null;

    const res = {...allPadsMetrics};
    delete res[currentRTFO?.name]
    return res
  }, [allPadsMetrics, currentRTFO?.name])

  let filteredAllPadsNames = filteredAllPadsMetrics ? Object.keys(filteredAllPadsMetrics) : [];
  filteredAllPadsNames = filteredAllPadsNames?.sort((a, b) => new Date(filteredAllPadsNames[b]?.createdOn)?.getTime() - new Date(filteredAllPadsNames[a]?.createdOn)?.getTime())

  const filteredAllPadsAreas = filteredAllPadsMetrics ? [...new Set(Object.keys(filteredAllPadsMetrics)?.map(padName => filteredAllPadsMetrics?.[padName]?.area))]?.sort((a, b) => a - b) : [];

  const dailyStages = useMemo(() => {
    let startTimestamp = new Date(reportDate);
    startTimestamp.setHours(6);
    startTimestamp.setMinutes(0);
    startTimestamp.setSeconds(0);
    startTimestamp.setMilliseconds(0);
    startTimestamp = startTimestamp.getTime();

    const endTimestamp = startTimestamp + 24 * 60 * 60 * 1000;

    const dailyStageNames = currentRTFO?.stages?.filter(s => currentRTFO?.stageData?.[s]?.stageEndTimestamp > startTimestamp && currentRTFO?.stageData?.[s]?.stageEndTimestamp < endTimestamp);

    return dailyStageNames;
  }, [currentRTFO?.stageData, currentRTFO?.stages, reportDate]);

  const pumpedStages = useMemo(() => {
    try {
      let pumpedStages = [...currentRTFO?.stages] || [];
      pumpedStages?.sort((a, b) => (currentRTFO?.stageData?.[a]?.stageStartTimestamp || 0) - (currentRTFO?.stageData?.[b]?.stageStartTimestamp || 0));
      return pumpedStages;
    } catch (error) {
      return [];
    };
  }, [currentRTFO?.stages, currentRTFO?.stageData]);

  const allStages = useMemo(() => {
    try {
      let allStages = [];
  
      for (const well in currentRTFO?.perfSchedule?.wells) {
        for (const stageNumber in currentRTFO?.perfSchedule?.wells?.[well]?.stages) {
          const stageName = `${currentRTFO?.name}_${well?.charAt(0)?.toUpperCase()?.concat(well?.substring(1, ))}_${stageNumber}`;
          allStages.push(stageName);
        };
      };
      
      allStages?.sort((a, b) => (currentRTFO?.stageData?.[a]?.stageStartTimestamp || 0) - (currentRTFO?.stageData?.[b]?.stageStartTimestamp || 0));
  
      return allStages;
      
    } catch (error) {
      return [];
    };
  }, [currentRTFO?.name, currentRTFO?.perfSchedule?.wells, currentRTFO?.stageData]);

  const [isLoadingPadsMetrics, setIsLoadingPadsMetrics] = useState(!filteredAllPadsMetrics ? true : false)
  const [displayPads, setDisplayPads] = useState([])

  const [isPlayblast, setIsPlayblast] = useState(false);
  const [metric, setMetric] = useState('Stress Shadow');
  const [seismicDataType, setSeismicDataType] = useState(currentRTFO?.seismicData?.highPrecision ? 'highPrecision' : currentRTFO?.seismicData?.realtime ? 'realtime' : 'off');
  const [fracLengthDisplay, setFracLengthDisplay] = useState(0.5);
  const [tilesType, setTilesType] = useState('map');
  const [seismicFilter, setSeismicFilter] = useState([]);
  const [isBufferZoneHidden, setIsBufferZoneHidden] = useState(currentRTFO?.seismicData?.realtime ? false : true);
  const [isFPZHidden, setIsFPZHidden] = useState(false);
  const [scrollZoomControlOverlayVisible, setScrollZoomControlOverlayVisible] = useState(false);


  const allPadsStages = useMemo(() => {
    try {
      let allPadsStages = []
      if (!filteredAllPadsMetrics || displayPads.length === 0) return allPadsStages
      displayPads?.forEach((padName) => {
        let padStages = []
        for (const well in filteredAllPadsMetrics[padName].perfSchedule?.wells) {
          for (const stageNumber in filteredAllPadsMetrics[padName].perfSchedule
            ?.wells?.[well]?.stages) {
            const stageName = `${padName}_${well?.charAt(0)?.toUpperCase()?.concat(well?.substring(1))}_${stageNumber}`
            padStages.push(stageName)
          }
        }
        padStages?.sort(
          (a, b) =>
            (currentRTFO?.stageData?.[a]?.stageStartTimestamp || 0) -
            (currentRTFO?.stageData?.[b]?.stageStartTimestamp || 0),
        )
        allPadsStages[padName] = padStages
      })

      return allPadsStages
    } catch (error) {
      console.log(error)
      return []
    }
  }, [filteredAllPadsMetrics, currentRTFO?.stageData, displayPads])

  const allMetrics = {
    'Stage Information': [
      'Stage Time',
      '# Clusters',
      'Cluster Spacing',
      'Stage Length',
      'Stage Sequence'
    ],
    'Pumping Information': [
      'Time to Pre Sand',
      'Propp. Placement Eff.',
      'Fluid Intensity',
      'Sand Per Fluid',
      'Fluid Per Sand',
      'DFR Per Sand',
      'DFR Per Fluid',
      'LFR Per Sand',
      'LFR Per Fluid',
      'Total DFR',
      'Total LFR',
      'Clean Vol. to Pre Sand',
      'Total Slurry Volume',
      'Total Clean Volume', 
      'Total Produced Volume', 
      'Overflush Vol.',
      'TPM Planned',
      'TPM Actual',
      'Total Proppant',
      'Max Proppant',
      'Average Pumping Pr.',
      'Average Pumping Rate'
    ],
    'ISIP': [
      'Breakdown Pressure',
      'ISIP Surf.',
      'ISIP BH',
      'Frac Gradient'
    ],
    'Stepdown Model': [
      'Pre Sand Pressure',
      'Pre Sand Rate',
      'Pre Sand Pnwb',
      'Pre Sand Pperf',
      'Flush Pperf',
      'Pre Sand Open Perfs',
      'Flush Open Perfs',
      'Perf Efficiency',
      'Rate Drop Events',
      'Plug Slip Events',
      'Sandoff Events',
      'Isolation Issue Events',
      'Equipment Issue Events',
      'Lost Suction Events',
      'Other Events',
      'Fracfeed Events'
    ],
    'Frac Model': [
      'Stress Shadow',
      'Seismicity Risk',
      'A*sqrt(k)',
      'Frac Area',
      'Half Length',
      'Percent Energy Lost',
      'dP Per Log Cycle'
    ],
    'FDI': [
      'Xf',
      'VFR',
    ],
  };
 
  useEffect(() => {
    if (isPlayblast) {
      console.log('Starting playblast...');
      const interval = setInterval(() => {
        let currentIndex = pumpedStages?.indexOf(currentStageName);
        if (currentIndex >= 0 && currentIndex < pumpedStages?.length - 1) {
          if (selectedWellName !== 'All') {
            while (currentIndex > 0 && currentIndex < pumpedStages?.length - 1 && !pumpedStages?.[currentIndex + 1]?.includes(selectedWellName)) {
              currentIndex++;
            };
          };
          dispatch(setCurrentStageName(pumpedStages?.[currentIndex + 1]));

        } else if (currentIndex === pumpedStages?.length - 1) {
          setIsPlayblast(false);
          clearInterval(interval)
        };

      }, 1000);
      return () => clearInterval(interval);
    };
  }, [pumpedStages, currentStageName, dispatch, isPlayblast, selectedWellName]);

  // stop loading indicator if 
  useEffect(() => {
    if (filteredAllPadsMetrics) {
      setIsLoadingPadsMetrics(false)
    }
  }, [filteredAllPadsMetrics])

  const wellCoordinates = useMemo(() => {
    try {
      const coords = {};
      const wells = currentRTFO?.surveys?.wells && Object.keys(currentRTFO?.surveys?.wells);
  
      if (!wells?.length) return coords;
  
      for (let i = 0; i < wells.length; i++) { // calculate well paths
        const lat = currentRTFO?.surveys?.wells?.[wells[i]]?.surfaceLat;
        const lon = currentRTFO?.surveys?.wells?.[wells[i]]?.surfaceLon;
        const surveyData = currentRTFO?.surveys?.wells?.[wells[i]]?.surveyData;
        const wellCoordinates = surveyToCoordinates(lat, lon, surveyData);
  
        if (!coords[wells[i]]) coords[wells[i]] = [];
  
        coords[wells[i]] = wellCoordinates;
      };
  
      return coords;
      
    } catch (error) {
      console.log(error);
      return {};
    };
  }, [currentRTFO?.surveys?.wells]);

  const allPadsWellCoordinates = useMemo(() => {
    try {
      let coords = {}
      if (!filteredAllPadsMetrics || displayPads.length === 0) return coords

      displayPads?.forEach((padName) => {
        let padCoords = {}
        let wells =
          filteredAllPadsMetrics[padName]?.surveys?.wells &&
          Object.keys(filteredAllPadsMetrics[padName]?.surveys?.wells)

        if (!wells?.length) return coords
        for (let i = 0; i < wells.length; i++) {
          // calculate well paths
          const lat =
            filteredAllPadsMetrics[padName]?.surveys?.wells?.[wells[i]]?.surfaceLat
          const lon =
            filteredAllPadsMetrics[padName]?.surveys?.wells?.[wells[i]]?.surfaceLon
          const surveyData =
            filteredAllPadsMetrics[padName]?.surveys?.wells?.[wells[i]]?.surveyData

          const singleWellCoordinate = surveyToCoordinates(lat, lon, surveyData)

          if (!padCoords[wells[i]]) padCoords[wells[i]] = []

          padCoords[wells[i]] = singleWellCoordinate
        }

        coords[padName] = padCoords
      })

      return coords
    } catch (error) {
      console.log(error)
      return {}
    }
  }, [filteredAllPadsMetrics, displayPads])

  const clusterCoordinates3D = useMemo(() => {
    try {
      const coords = {};

      for (let j = 0; j < allStages?.length; j++) { // calculate stage/cluster lines
        
        const stageName = allStages[j];
        const stageNumber = String(stageName?.split('_')?.[2])?.replace(/[^0-9]+/g, '');
        const wellName = stageName?.split('_')?.[1]?.toLowerCase();
        if (!currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]) continue;
        
        const fracLength = ((currentRTFO?.stageData?.[stageName]?.fracModel?.halfLength * 2) || (currentRTFO?.stageData?.[stageName]?.fracModel?.fracArea / (currentRTFO?.settings?.fracHeight)) || 600) * fracLengthDisplay;
  
        const clusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters ? Object.keys(currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters) : [];

        for (let k = 0; k < clusters?.length; k++) {
          const clusterName = clusters[k];
          const md = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters?.[clusters[k]]?.shotDepth;
          const surveyData = currentRTFO?.surveys?.wells?.[wellName]?.surveyData;
          const wellLatLngs = wellCoordinates[wellName]?.map(c => [c.lat, c.lon]);
          const clusterLatLon = interpolateCoordinates(surveyData, wellLatLngs, md);
          let clusterLineCoordinates = calculateLineCoordinates(clusterLatLon, fracLength);
          clusterLineCoordinates = rotatePolyline(clusterLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));
          
          if (!coords[stageName]) coords[stageName] = {};
  
          coords[stageName][clusterName] = {
            top: { // top of the frac plane
              start: { // start of the frac line along the plane top
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              },
              center: { // center of the frac line along the plane top
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              },
              end: { // end of the frac line along the plane top
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              }
            },
            center: { // center of the frac plane (corresponds to stage TVD)
              start: {
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md)
              },
              center: {
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md)
              },
              end: {
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md)
              }
            },
            bottom: {
              start: {
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              },
              center: {
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              },
              end: {
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              }
            }
          };
        };
      };
  
      return coords;
      
    } catch (error) {
      console.log(error);
      return {};
    };

  }, [allStages, currentRTFO?.stageData, currentRTFO?.settings, currentRTFO?.perfSchedule?.wells, currentRTFO?.surveys?.wells, wellCoordinates, fracLengthDisplay]);

  const allPadsClusterCoordinates3D = useMemo(() => {
    try {
      let coords = {}
      if (!filteredAllPadsMetrics || displayPads?.length === 0) return coords

      displayPads?.forEach((padName) => {
        let padCoords = {}
        for (let j = 0; j < allPadsStages[padName]?.length; j++) {
          // calculate stage/cluster lines
          let stageName = allPadsStages[padName]?.[j]
          let stageNumber = String(stageName?.split('_')?.[2])?.replace(
            /[^0-9]+/g, //index of stage
            '',
          )
          let wellName = stageName?.split('_')?.[1]?.toLowerCase()

          if (
            !filteredAllPadsMetrics[padName]?.perfSchedule?.wells?.[wellName]?.stages?.[
              stageNumber
            ]
          ) {
            continue
          }

          const fracLength = (
            (filteredAllPadsMetrics[padName]?.metrics?.[stageName]?.halfLength * 2) ||
            (filteredAllPadsMetrics[padName]?.metrics?.[stageName]?.fracArea / filteredAllPadsMetrics[padName]?.settings?.fracHeight) || 600) *
            fracLengthDisplay

          const clusters = filteredAllPadsMetrics[padName]?.perfSchedule?.wells?.[
            wellName
          ]?.stages?.[stageNumber]?.clusters
            ? Object.keys(
                filteredAllPadsMetrics[padName]?.perfSchedule?.wells?.[wellName]
                  ?.stages?.[stageNumber]?.clusters,
              )
            : []

          for (let k = 0; k < clusters?.length; k++) {
            let clusterName = clusters[k]

            let md =
              filteredAllPadsMetrics[padName]?.perfSchedule?.wells?.[wellName]
                ?.stages?.[stageNumber]?.clusters?.[clusters[k]]?.shotDepth
            let surveyData =
              filteredAllPadsMetrics[padName]?.surveys?.wells?.[wellName]?.surveyData
            let wellLatLngs = allPadsWellCoordinates?.[padName]?.[
              wellName
            ]?.map((c) => [c.lat, c.lon])
            let clusterLatLon = interpolateCoordinates(
              surveyData,
              wellLatLngs,
              md,
            )
            let clusterLineCoordinates = calculateLineCoordinates(
              clusterLatLon,
              fracLength,
            )
            clusterLineCoordinates = rotatePolyline(
              clusterLineCoordinates,
              filteredAllPadsMetrics[padName]?.settings?.fracAngle || 45,
            )

            if (!padCoords[stageName]) padCoords[stageName] = {}

            padCoords[stageName][clusterName] = {
              top: {
                // top of the frac plane
                start: {
                  // start of the frac line along the plane top
                  lat: clusterLineCoordinates?.[0]?.[0],
                  lon: clusterLineCoordinates?.[0]?.[1],
                  // TVD - True Vertical Depth
                  tvd:
                    findTVD(surveyData, md) +
                      filteredAllPadsMetrics[padName]?.settings?.fracHeight / 2 || 0,
                },
                center: {
                  // center of the frac line along the plane top
                  lat: clusterLineCoordinates?.[1]?.[0],
                  lon: clusterLineCoordinates?.[1]?.[1],
                  tvd:
                    findTVD(surveyData, md) +
                      filteredAllPadsMetrics[padName]?.settings?.fracHeight / 2 || 0,
                },
                end: {
                  // end of the frac line along the plane top
                  lat: clusterLineCoordinates?.[2]?.[0],
                  lon: clusterLineCoordinates?.[2]?.[1],
                  tvd:
                    findTVD(surveyData, md) +
                      filteredAllPadsMetrics[padName]?.settings?.fracHeight / 2 || 0,
                },
              },
              center: {
                // center of the frac plane (corresponds to stage TVD)
                start: {
                  lat: clusterLineCoordinates?.[0]?.[0],
                  lon: clusterLineCoordinates?.[0]?.[1],
                  tvd: findTVD(surveyData, md) || 0,
                },
                center: {
                  lat: clusterLineCoordinates?.[1]?.[0],
                  lon: clusterLineCoordinates?.[1]?.[1],
                  tvd: findTVD(surveyData, md) || 0,
                },
                end: {
                  lat: clusterLineCoordinates?.[2]?.[0],
                  lon: clusterLineCoordinates?.[2]?.[1],
                  tvd: findTVD(surveyData, md) || 0,
                },
              },
              bottom: {
                start: {
                  lat: clusterLineCoordinates?.[0]?.[0],
                  lon: clusterLineCoordinates?.[0]?.[1],
                  tvd:
                    findTVD(surveyData, md) -
                      filteredAllPadsMetrics[padName]?.settings?.fracHeight / 2 || 0,
                },
                center: {
                  lat: clusterLineCoordinates?.[1]?.[0],
                  lon: clusterLineCoordinates?.[1]?.[1],
                  tvd:
                    findTVD(surveyData, md) -
                      filteredAllPadsMetrics[padName]?.settings?.fracHeight / 2 || 0,
                },
                end: {
                  lat: clusterLineCoordinates?.[2]?.[0],
                  lon: clusterLineCoordinates?.[2]?.[1],
                  tvd:
                    findTVD(surveyData, md) -
                      filteredAllPadsMetrics[padName]?.settings?.fracHeight / 2 || 0,
                },
              },
            }
          }
        }
        coords[padName] = padCoords
      })

      return coords
    } catch (error) {
      console.log(error)
      return {}
    }
  }, [
    filteredAllPadsMetrics,
    displayPads,
    allPadsStages,
    fracLengthDisplay,
    allPadsWellCoordinates,
  ])

  const clusterCoordinatesSS = useMemo(() => {
    try {
      const coords = {};

      for (let j = 0; j < allStages?.length; j++) { // calculate stage/cluster lines
        
        const stageName = allStages[j];
        const stageNumber = String(stageName?.split('_')?.[2])?.replace(/[^0-9]+/g, '');
        const wellName = stageName?.split('_')?.[1]?.toLowerCase();
        if (!currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]) continue;
        
        const fracLength = currentRTFO?.stageData?.[stageName]?.fracModel?.halfLength || (currentRTFO?.stageData?.[stageName]?.fracModel?.fracArea / (currentRTFO?.settings?.fracHeight * 2)) || 300;
  
        const clusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters ? Object.keys(currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters) : [];
        
        for (let k = 0; k < clusters?.length; k++) {
          const clusterName = clusters[k];
          const md = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters?.[clusters[k]]?.shotDepth;
          const surveyData = currentRTFO?.surveys?.wells?.[wellName]?.surveyData;
          const wellLatLngs = wellCoordinates[wellName]?.map(c => [c.lat, c.lon]);
          const clusterLatLon = interpolateCoordinates(surveyData, wellLatLngs, md);
          let clusterLineCoordinates = calculateLineCoordinates(clusterLatLon, fracLength);
          clusterLineCoordinates = rotatePolyline(clusterLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));
          // let clusterLineCoordinates = getLineCoordinates(clusterLatLon, fracLength);
          // clusterLineCoordinates = rotateLine(clusterLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));
    
          if (!coords[stageName]) coords[stageName] = {};
  
          coords[stageName][clusterName] = {
            top: { // top of the frac plane
              start: { // start of the frac line along the plane top
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              },
              center: { // center of the frac line along the plane top
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              },
              end: { // end of the frac line along the plane top
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md) + currentRTFO?.settings?.fracHeight / 2
              }
            },
            center: { // center of the frac plane (corresponds to stage TVD)
              start: {
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md)
              },
              center: {
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md)
              },
              end: {
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md)
              }
            },
            bottom: {
              start: {
                lat: clusterLineCoordinates?.[0]?.[0],
                lon: clusterLineCoordinates?.[0]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              },
              center: {
                lat: clusterLineCoordinates?.[1]?.[0],
                lon: clusterLineCoordinates?.[1]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              },
              end: {
                lat: clusterLineCoordinates?.[2]?.[0],
                lon: clusterLineCoordinates?.[2]?.[1],
                tvd: findTVD(surveyData, md) - currentRTFO?.settings?.fracHeight / 2
              }
            }
          };
        };
      };
  
      return coords;
      
    } catch (error) {
      console.log(error);
      return {};
    };

  }, [allStages, currentRTFO?.stageData, currentRTFO?.settings, currentRTFO?.perfSchedule?.wells, currentRTFO?.surveys?.wells, wellCoordinates]);

  const allPadsClusterCoordinatesSS = useMemo(() => {
    try {
      let coords = {}
      if (!filteredAllPadsMetrics || displayPads?.length === 0) return coords

      displayPads?.forEach((padName) => {
        let padCoords = {}
        for (let j = 0; j < allPadsStages[padName]?.length; j++) {
          // calculate stage/cluster lines
          let stageName = allPadsStages[padName]?.[j]
          let stageNumber = String(stageName?.split('_')?.[2])?.replace(
            /[^0-9]+/g, //index of stage
            '',
          )
          let wellName = stageName?.split('_')?.[1]?.toLowerCase()

          if (
            !filteredAllPadsMetrics[padName]?.perfSchedule?.wells?.[wellName]?.stages?.[
              stageNumber
            ]
          ) {
            continue
          }

          const fracLength = (
            (filteredAllPadsMetrics[padName]?.metrics?.[stageName]?.halfLength) ||
            (filteredAllPadsMetrics[padName]?.metrics?.[stageName]?.fracArea / (2 * filteredAllPadsMetrics[padName]?.settings?.fracHeight)) || 300)
            
          const clusters = filteredAllPadsMetrics[padName]?.perfSchedule?.wells?.[
            wellName
          ]?.stages?.[stageNumber]?.clusters
            ? Object.keys(
                filteredAllPadsMetrics[padName]?.perfSchedule?.wells?.[wellName]
                  ?.stages?.[stageNumber]?.clusters,
              )
            : []

          for (let k = 0; k < clusters?.length; k++) {
            let clusterName = clusters[k]

            let md =
              filteredAllPadsMetrics[padName]?.perfSchedule?.wells?.[wellName]
                ?.stages?.[stageNumber]?.clusters?.[clusters[k]]?.shotDepth
            let surveyData =
              filteredAllPadsMetrics[padName]?.surveys?.wells?.[wellName]?.surveyData
            let wellLatLngs = allPadsWellCoordinates?.[padName]?.[
              wellName
            ]?.map((c) => [c.lat, c.lon])
            let clusterLatLon = interpolateCoordinates(
              surveyData,
              wellLatLngs,
              md,
            )
            let clusterLineCoordinates = calculateLineCoordinates(
              clusterLatLon,
              fracLength,
            )
            clusterLineCoordinates = rotatePolyline(
              clusterLineCoordinates,
              filteredAllPadsMetrics[padName]?.settings?.fracAngle || 45,
            )

            if (!padCoords[stageName]) padCoords[stageName] = {}

            padCoords[stageName][clusterName] = {
              top: {
                // top of the frac plane
                start: {
                  // start of the frac line along the plane top
                  lat: clusterLineCoordinates?.[0]?.[0],
                  lon: clusterLineCoordinates?.[0]?.[1],
                  // TVD - True Vertical Depth
                  tvd:
                    findTVD(surveyData, md) +
                      filteredAllPadsMetrics[padName]?.settings?.fracHeight / 2 || 0,
                },
                center: {
                  // center of the frac line along the plane top
                  lat: clusterLineCoordinates?.[1]?.[0],
                  lon: clusterLineCoordinates?.[1]?.[1],
                  tvd:
                    findTVD(surveyData, md) +
                      filteredAllPadsMetrics[padName]?.settings?.fracHeight / 2 || 0,
                },
                end: {
                  // end of the frac line along the plane top
                  lat: clusterLineCoordinates?.[2]?.[0],
                  lon: clusterLineCoordinates?.[2]?.[1],
                  tvd:
                    findTVD(surveyData, md) +
                      filteredAllPadsMetrics[padName]?.settings?.fracHeight / 2 || 0,
                },
              },
              center: {
                // center of the frac plane (corresponds to stage TVD)
                start: {
                  lat: clusterLineCoordinates?.[0]?.[0],
                  lon: clusterLineCoordinates?.[0]?.[1],
                  tvd: findTVD(surveyData, md) || 0,
                },
                center: {
                  lat: clusterLineCoordinates?.[1]?.[0],
                  lon: clusterLineCoordinates?.[1]?.[1],
                  tvd: findTVD(surveyData, md) || 0,
                },
                end: {
                  lat: clusterLineCoordinates?.[2]?.[0],
                  lon: clusterLineCoordinates?.[2]?.[1],
                  tvd: findTVD(surveyData, md) || 0,
                },
              },
              bottom: {
                start: {
                  lat: clusterLineCoordinates?.[0]?.[0],
                  lon: clusterLineCoordinates?.[0]?.[1],
                  tvd:
                    findTVD(surveyData, md) -
                      filteredAllPadsMetrics[padName]?.settings?.fracHeight / 2 || 0,
                },
                center: {
                  lat: clusterLineCoordinates?.[1]?.[0],
                  lon: clusterLineCoordinates?.[1]?.[1],
                  tvd:
                    findTVD(surveyData, md) -
                      filteredAllPadsMetrics[padName]?.settings?.fracHeight / 2 || 0,
                },
                end: {
                  lat: clusterLineCoordinates?.[2]?.[0],
                  lon: clusterLineCoordinates?.[2]?.[1],
                  tvd:
                    findTVD(surveyData, md) -
                      filteredAllPadsMetrics[padName]?.settings?.fracHeight / 2 || 0,
                },
              },
            }
          }
        }
        coords[padName] = padCoords
      })

      return coords
    } catch (error) {
      console.log(error)
      return {}
    }
  }, [filteredAllPadsMetrics, displayPads, allPadsStages, allPadsWellCoordinates])

  const stageCoordinates3D = useMemo(() => {
    try {
      const coords = {};
  
      Object.keys(clusterCoordinates3D)?.forEach(stageName => {
        const clusterCoords = Object.values(clusterCoordinates3D[stageName]);
  
        const firstCluster = clusterCoords?.[0]?.center; // coordinates of the first stage cluster at center tvd
        const lastCluster = clusterCoords?.[clusterCoords?.length - 1]?.center; // coordinates of the last stage cluster at center tvd
        const startLatLon = [[firstCluster?.start?.lat, firstCluster?.start?.lon], [firstCluster?.center?.lat, firstCluster?.center?.lon], [firstCluster?.end?.lat, firstCluster?.end?.lon]];
        const endLatLon = [[lastCluster?.start?.lat, lastCluster?.start?.lon], [lastCluster?.center?.lat, lastCluster?.center?.lon], [lastCluster?.end?.lat, lastCluster?.end?.lon]];
        
        const averageTVD = (firstCluster?.center?.tvd + lastCluster?.center?.tvd) / 2;
  
        const stageLatLon = getMiddlePoint([startLatLon, endLatLon]); // coordinates of the center of the stage
  
        const fracLength = ((currentRTFO?.stageData?.[stageName]?.fracModel?.halfLength * 2) || (currentRTFO?.stageData?.[stageName]?.fracModel?.fracArea / (currentRTFO?.settings?.fracHeight)) || 600) * fracLengthDisplay;
        
        let stageLineCoordinates = calculateLineCoordinates(stageLatLon, fracLength);
        stageLineCoordinates = rotatePolyline(stageLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));
        // let stageLineCoordinates = getLineCoordinates(stageLatLon, fracLength);
        // stageLineCoordinates = rotateLine(stageLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));
        coords[stageName] = {
          top: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            }
          },
          center: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD
            }
          },
          bottom: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            }
          }
        };
      });
  
      return coords;
      
    } catch (error) {
      console.log(error);
      return {};
    };
    
  }, [clusterCoordinates3D, currentRTFO?.stageData, currentRTFO?.settings, fracLengthDisplay]);

  const allPadsStageCoordinates3D = useMemo(() => {
    try {
      const coords = {}
      if (!filteredAllPadsMetrics || displayPads?.length === 0) return coords

      for (const padName of displayPads) {
        const padClusterCoordinates3D = allPadsClusterCoordinates3D?.[padName]

        const padCoords = {}

        Object.keys(padClusterCoordinates3D)?.forEach((stageName) => {
          const clusterCoords = Object.values(
            padClusterCoordinates3D[stageName],
          )

          const firstCluster = clusterCoords?.[0]?.center // coordinates of the first stage cluster at center tvd
          const lastCluster = clusterCoords?.[clusterCoords?.length - 1]?.center // coordinates of the last stage cluster at center tvd
          const startLatLon = [
            [firstCluster?.start?.lat, firstCluster?.start?.lon],
            [firstCluster?.center?.lat, firstCluster?.center?.lon],
            [firstCluster?.end?.lat, firstCluster?.end?.lon],
          ]
          const endLatLon = [
            [lastCluster?.start?.lat, lastCluster?.start?.lon],
            [lastCluster?.center?.lat, lastCluster?.center?.lon],
            [lastCluster?.end?.lat, lastCluster?.end?.lon],
          ]

          const averageTVD =
            (firstCluster?.center?.tvd + lastCluster?.center?.tvd) / 2

          const stageLatLon = getMiddlePoint([startLatLon, endLatLon]) // coordinates of the center of the stage

          const fracLength = (
            (filteredAllPadsMetrics[padName]?.metrics?.[stageName]?.halfLength * 2) ||
            (filteredAllPadsMetrics[padName]?.metrics?.[stageName]?.fracArea / filteredAllPadsMetrics[padName]?.settings?.fracHeight) || 600) *
            fracLengthDisplay

          let stageLineCoordinates = calculateLineCoordinates(
            stageLatLon,
            fracLength,
          )
          stageLineCoordinates = rotatePolyline(
            stageLineCoordinates,
            filteredAllPadsMetrics?.[padName]?.settings?.fracAngle || 45,
          )

          padCoords[stageName] = {
            top: {
              start: {
                lat: stageLineCoordinates?.[0]?.[0],
                lon: stageLineCoordinates?.[0]?.[1],
                tvd:
                  averageTVD +
                  filteredAllPadsMetrics?.[padName]?.settings?.fracHeight / 2,
              },
              center: {
                lat: stageLineCoordinates?.[1]?.[0],
                lon: stageLineCoordinates?.[1]?.[1],
                tvd:
                  averageTVD +
                  filteredAllPadsMetrics?.[padName]?.settings?.fracHeight / 2,
              },
              end: {
                lat: stageLineCoordinates?.[2]?.[0],
                lon: stageLineCoordinates?.[2]?.[1],
                tvd:
                  averageTVD +
                  filteredAllPadsMetrics?.[padName]?.settings?.fracHeight / 2,
              },
            },
            center: {
              start: {
                lat: stageLineCoordinates?.[0]?.[0],
                lon: stageLineCoordinates?.[0]?.[1],
                tvd: averageTVD,
              },
              center: {
                lat: stageLineCoordinates?.[1]?.[0],
                lon: stageLineCoordinates?.[1]?.[1],
                tvd: averageTVD,
              },
              end: {
                lat: stageLineCoordinates?.[2]?.[0],
                lon: stageLineCoordinates?.[2]?.[1],
                tvd: averageTVD,
              },
            },
            bottom: {
              start: {
                lat: stageLineCoordinates?.[0]?.[0],
                lon: stageLineCoordinates?.[0]?.[1],
                tvd:
                  averageTVD -
                  filteredAllPadsMetrics?.[padName]?.settings?.fracHeight / 2,
              },
              center: {
                lat: stageLineCoordinates?.[1]?.[0],
                lon: stageLineCoordinates?.[1]?.[1],
                tvd:
                  averageTVD -
                  filteredAllPadsMetrics?.[padName]?.settings?.fracHeight / 2,
              },
              end: {
                lat: stageLineCoordinates?.[2]?.[0],
                lon: stageLineCoordinates?.[2]?.[1],
                tvd:
                  averageTVD -
                  filteredAllPadsMetrics?.[padName]?.settings?.fracHeight / 2,
              },
            },
          }
        })

        coords[padName] = padCoords
      }

      return coords
    } catch (error) {
      console.log(error)
      return {}
    }
  }, [
    filteredAllPadsMetrics,
    displayPads,
    allPadsClusterCoordinates3D,
    fracLengthDisplay,
  ])

  const stageCoordinatesSS = useMemo(() => {
    try {
      const coords = {};
  
      Object.keys(clusterCoordinatesSS)?.forEach(stageName => {
        const clusterCoords = Object.values(clusterCoordinatesSS[stageName]);
  
        const firstCluster = clusterCoords?.[0]?.center; // coordinates of the first stage cluster at center tvd
        const lastCluster = clusterCoords?.[clusterCoords?.length - 1]?.center; // coordinates of the last stage cluster at center tvd
        const startLatLon = [[firstCluster?.start?.lat, firstCluster?.start?.lon], [firstCluster?.center?.lat, firstCluster?.center?.lon], [firstCluster?.end?.lat, firstCluster?.end?.lon]];
        const endLatLon = [[lastCluster?.start?.lat, lastCluster?.start?.lon], [lastCluster?.center?.lat, lastCluster?.center?.lon], [lastCluster?.end?.lat, lastCluster?.end?.lon]];
        
        const averageTVD = (firstCluster?.center?.tvd + lastCluster?.center?.tvd) / 2;
  
        const stageLatLon = getMiddlePoint([startLatLon, endLatLon]); // coordinates of the center of the stage
  
        const fracLength = currentRTFO?.stageData?.[stageName]?.fracModel?.halfLength || (currentRTFO?.stageData?.[stageName]?.fracModel?.fracArea / (currentRTFO?.settings?.fracHeight * 2)) || 300;
        
        let stageLineCoordinates = calculateLineCoordinates(stageLatLon, fracLength);
        stageLineCoordinates = rotatePolyline(stageLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));
        // let stageLineCoordinates = getLineCoordinates(stageLatLon, fracLength);
        // stageLineCoordinates = rotateLine(stageLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));
        coords[stageName] = {
          top: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD + currentRTFO?.settings?.fracHeight / 2
            }
          },
          center: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD
            }
          },
          bottom: {
            start: {
              lat: stageLineCoordinates?.[0]?.[0],
              lon: stageLineCoordinates?.[0]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            },
            center: {
              lat: stageLineCoordinates?.[1]?.[0],
              lon: stageLineCoordinates?.[1]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            },
            end: {
              lat: stageLineCoordinates?.[2]?.[0],
              lon: stageLineCoordinates?.[2]?.[1],
              tvd: averageTVD - currentRTFO?.settings?.fracHeight / 2
            }
          }
        };
      });
  
      return coords;
      
    } catch (error) {
      console.log(error);
      return {};
    };
    
  }, [clusterCoordinatesSS, currentRTFO?.stageData, currentRTFO?.settings]);

  const allPadsStageCoordinatesSS = useMemo(() => {
    try {
      const coords = {}
      if (!filteredAllPadsMetrics || displayPads?.length === 0) return coords

      for (const padName of displayPads) {
        const padClusterCoordinates3D = allPadsClusterCoordinates3D?.[padName]

        const padCoords = {}

        Object.keys(padClusterCoordinates3D)?.forEach((stageName) => {
          const clusterCoords = Object.values(
            padClusterCoordinates3D[stageName],
          )

          const firstCluster = clusterCoords?.[0]?.center // coordinates of the first stage cluster at center tvd
          const lastCluster = clusterCoords?.[clusterCoords?.length - 1]?.center // coordinates of the last stage cluster at center tvd
          const startLatLon = [
            [firstCluster?.start?.lat, firstCluster?.start?.lon],
            [firstCluster?.center?.lat, firstCluster?.center?.lon],
            [firstCluster?.end?.lat, firstCluster?.end?.lon],
          ]
          const endLatLon = [
            [lastCluster?.start?.lat, lastCluster?.start?.lon],
            [lastCluster?.center?.lat, lastCluster?.center?.lon],
            [lastCluster?.end?.lat, lastCluster?.end?.lon],
          ]

          const averageTVD =
            (firstCluster?.center?.tvd + lastCluster?.center?.tvd) / 2

          const stageLatLon = getMiddlePoint([startLatLon, endLatLon]) // coordinates of the center of the stage

          const fracLength = (
            (filteredAllPadsMetrics[padName]?.metrics?.[stageName]?.halfLength) ||
            (filteredAllPadsMetrics[padName]?.metrics?.[stageName]?.fracArea / (2 * filteredAllPadsMetrics[padName]?.settings?.fracHeight)) || 300);

          let stageLineCoordinates = calculateLineCoordinates(
            stageLatLon,
            fracLength,
          )
          stageLineCoordinates = rotatePolyline(
            stageLineCoordinates,
            filteredAllPadsMetrics?.[padName]?.settings?.fracAngle || 45,
          )

          padCoords[stageName] = {
            top: {
              start: {
                lat: stageLineCoordinates?.[0]?.[0],
                lon: stageLineCoordinates?.[0]?.[1],
                tvd:
                  averageTVD +
                  filteredAllPadsMetrics?.[padName]?.settings?.fracHeight / 2,
              },
              center: {
                lat: stageLineCoordinates?.[1]?.[0],
                lon: stageLineCoordinates?.[1]?.[1],
                tvd:
                  averageTVD +
                  filteredAllPadsMetrics?.[padName]?.settings?.fracHeight / 2,
              },
              end: {
                lat: stageLineCoordinates?.[2]?.[0],
                lon: stageLineCoordinates?.[2]?.[1],
                tvd:
                  averageTVD +
                  filteredAllPadsMetrics?.[padName]?.settings?.fracHeight / 2,
              },
            },
            center: {
              start: {
                lat: stageLineCoordinates?.[0]?.[0],
                lon: stageLineCoordinates?.[0]?.[1],
                tvd: averageTVD,
              },
              center: {
                lat: stageLineCoordinates?.[1]?.[0],
                lon: stageLineCoordinates?.[1]?.[1],
                tvd: averageTVD,
              },
              end: {
                lat: stageLineCoordinates?.[2]?.[0],
                lon: stageLineCoordinates?.[2]?.[1],
                tvd: averageTVD,
              },
            },
            bottom: {
              start: {
                lat: stageLineCoordinates?.[0]?.[0],
                lon: stageLineCoordinates?.[0]?.[1],
                tvd:
                  averageTVD -
                  filteredAllPadsMetrics?.[padName]?.settings?.fracHeight / 2,
              },
              center: {
                lat: stageLineCoordinates?.[1]?.[0],
                lon: stageLineCoordinates?.[1]?.[1],
                tvd:
                  averageTVD -
                  filteredAllPadsMetrics?.[padName]?.settings?.fracHeight / 2,
              },
              end: {
                lat: stageLineCoordinates?.[2]?.[0],
                lon: stageLineCoordinates?.[2]?.[1],
                tvd:
                  averageTVD -
                  filteredAllPadsMetrics?.[padName]?.settings?.fracHeight / 2,
              },
            },
          }
        })

        coords[padName] = padCoords
      }

      return coords
    } catch (error) {
      console.log(error)
      return {}
    }
  }, [filteredAllPadsMetrics, displayPads, allPadsClusterCoordinates3D])

  const bufferZoneCoordinates = useMemo(() => {
    try {
      if (!wellCoordinates || Object.keys(wellCoordinates)?.length === 0) return []
  
      const bufferZoneCoord = []
      const buffers = []
  
      for (const wellName of Object.keys(wellCoordinates)) {
        for (const point of wellCoordinates[wellName]) {
          const center = [point?.lon, point?.lat];
          const radius = 5;
          const options = {steps: 64, units: 'kilometers'};
          const circle = turf.circle(center, radius, options);
          buffers.push(circle);
        }
      }
  
      // Combine all circle geometries
      const combinedCircles = turf.combine(turf.featureCollection(buffers));

      // Create a convex hull from the combined circles
      const convexHull = turf.convex(combinedCircles);

      for (const point of convexHull?.geometry?.coordinates?.[0]) {
        bufferZoneCoord.push([point[1], point[0]])
      }
  
      return bufferZoneCoord;

    } catch (error) {
      console.log(error)
    }

  }, [wellCoordinates]);

  function createOuterPerimeter(coordinates) {
    // Ensure the coordinates are in a FeatureCollection format for Turf.js
    const points = coordinates.map((coord) => turf.point([coord[1], coord[0]]))
    const featureCollection = turf.featureCollection(points)

    // Calculate the convex hull, which is the minimal convex shape enclosing all points
    const convexHull = turf.convex(featureCollection)

    // If there's no convex hull, return an empty array
    if (!convexHull) return []

    // Extract the coordinates from the convex hull and revert them to the original order ([lat, lon])
    const outerPerimeter = convexHull.geometry.coordinates[0].map((coord) => [
      coord[1],
      coord[0],
    ])

    return outerPerimeter
  }

  // Function to create buffer zones for each coordinate point of a well
  function createBufferZones(wellCoords, radius) {
    return wellCoords.map((point) => {
      const center = [point.lon, point.lat]
      return turf.circle(center, radius, { steps: 64, units: 'kilometers' })
    })
  }

  // Function to combine buffer zones and create a convex hull
  function combineBuffersAndCreateConvexHull(buffers) {
    const combinedCircles = turf.combine(turf.featureCollection(buffers))
    return turf.convex(combinedCircles)
  }

  // Function to add unique coordinates to the set
  function addUniqueCoordinates(coordSet, coords) {
    coords.forEach((coord) => {
      const coordStr = JSON.stringify(coord)
      if (!coordSet.has(coordStr)) {
        coordSet.add(coordStr)
      }
    })
  }

  const fpzCoordinates = useMemo(() => {
    try {
      if (!wellCoordinates || Object.keys(wellCoordinates)?.length === 0)
        return []

      const fpzCoord = []
      const buffers = []

      for (const wellName of Object.keys(wellCoordinates)) {
        for (const point of wellCoordinates[wellName]) {
          const center = [point?.lon, point?.lat]
          const radius = 1
          const options = { steps: 64, units: 'kilometers' }
          const circle = turf.circle(center, radius, options)
          buffers.push(circle)
        }
      }

      // Combine all circle geometries
      const combinedCircles = turf.combine(turf.featureCollection(buffers))

      // Create a convex hull from the combined circles
      const convexHull = turf.convex(combinedCircles)

      for (const point of convexHull?.geometry?.coordinates?.[0] || []) {
        fpzCoord.push([point[1], point[0]])
      }

      // console.log({ fpzCoord: fpzCoord })
      return fpzCoord
    } catch (error) {
      console.log(error)
    }
  }, [wellCoordinates])

  const allPadsFpzCoordinates = useMemo(() => {
    const allFpzCoord = {}
    if (!filteredAllPadsMetrics || displayPads?.length === 0) return allFpzCoord

    displayPads.forEach((padId) => {
      const wellsOfAllPadsSurvey = filteredAllPadsMetrics[padId].surveys?.wells

      let uniqueCoordsSet = new Set()

      Object.values(wellsOfAllPadsSurvey).forEach((wellData) => {
        const wellCoords = surveyToCoordinates(
          wellData.surfaceLat,
          wellData.surfaceLon,
          wellData.surveyData,
        )

        // Skip if there are no well coordinates
        if (!wellCoords || wellCoords.length === 0) return

        // Create FPZ buffer zones with a radius of 1
        const buffers = createBufferZones(wellCoords, 1) // Using radius of 1 for FPZ
        const convexHull = combineBuffersAndCreateConvexHull(buffers)
        const hullCoords = convexHull.geometry.coordinates[0].map((coord) => [
          coord[1],
          coord[0],
        ])

        // Add unique coordinates to the Set
        addUniqueCoordinates(uniqueCoordsSet, hullCoords)
      })

      // Convert uniqueCoordsSet to array and refine FPZ for each pad
      allFpzCoord[padId] = createOuterPerimeter(
        Array.from(uniqueCoordsSet).map((coordStr) => JSON.parse(coordStr)),
      )
    })

    return allFpzCoord
  }, [filteredAllPadsMetrics, displayPads])

  // STRESS SHADOW WORKFLOW
  const stressShadow = useMemo(() => {
    try {
      let ss = {};

      const currentStageIndex = pumpedStages?.indexOf(currentStageName);
      const previousStages = pumpedStages?.slice(0, currentStageIndex + 1);

      for (let i = 0; i < previousStages?.length; i++) {
        const stageName = pumpedStages[i];
        // iterate through all stages and calculate shadow for each stage within the radius
        ss = calculateStressShadow(currentRTFO?.stageData, pumpedStages, ss, stageCoordinatesSS, clusterCoordinatesSS, stageName, currentRTFO?.perfSchedule, currentRTFO?.settings, currentRTFO?.formation);
      };
  
      return ss;
      
    } catch (error) {
      console.log(error);
    };

  }, [currentStageName, clusterCoordinatesSS, currentRTFO?.stageData, currentRTFO?.formation, currentRTFO?.perfSchedule, currentRTFO?.settings, stageCoordinatesSS, pumpedStages]);
  
  const allPadsStressShadow = useMemo(() => {
    try {
      let ss = {}
      if (!filteredAllPadsMetrics || displayPads?.length === 0) return ss

      for (const padName of displayPads) {
        let padSS = {}
        const padStages = allPadsStages?.[padName]

        const currentStageIndex = padStages?.length - 1
        const previousStages = padStages?.slice(0, currentStageIndex + 1)

        for (let i = 0; i < previousStages?.length; i++) {
          const stageName = padStages[i]
          // iterate through all stages and calculate shadow for each stage within the radius
          padSS = calculateStressShadow(
            filteredAllPadsMetrics?.[padName]?.metrics,
            padStages,
            padSS,
            allPadsStageCoordinatesSS?.[padName],
            allPadsClusterCoordinatesSS?.[padName],
            stageName,
            filteredAllPadsMetrics?.[padName]?.perfSchedule,
            filteredAllPadsMetrics?.[padName]?.settings,
            filteredAllPadsMetrics?.[padName]?.formation,
          )
        }

        ss[padName] = padSS
      }

      return ss
    } catch (error) {
      console.log(error)
    }
  }, [
    filteredAllPadsMetrics,
    displayPads,
    allPadsStages,
    allPadsStageCoordinatesSS,
    allPadsClusterCoordinatesSS,
  ])

  // CALCULATE INTERSECTION LINE COORDINATES
  const stageIntersectionLineCoordinates = useMemo(() => {
    
    if (!currentStageName) return [];

    const stageLatLon = [stageCoordinates3D?.[currentStageName]?.center?.center?.lat, stageCoordinates3D?.[currentStageName]?.center?.center?.lon];
    let stageLineCoordinates = calculateLineCoordinates(stageLatLon, 5000);
    stageLineCoordinates = rotatePolyline(stageLineCoordinates, (currentRTFO?.settings?.fracAngle || 45)); // use 5 km line to find intersections
    // stageLineCoordinates = rotateLine(getLineCoordinates(stageLatLon, 5000), (currentRTFO?.settings?.fracAngle || 45)); // use 5 km line to find intersections
    return stageLineCoordinates;
  }, [stageCoordinates3D, currentStageName, currentRTFO?.settings?.fracAngle]);

  // CALCULATE GLOBAL MAP CENTER
  const globalCenter = useMemo(() => {
    try {
      const center = turf.center(turf.points(bufferZoneCoordinates));
      return {lat: center?.geometry?.coordinates?.[0] || 0, lon: center?.geometry?.coordinates?.[1] || 0};
    } catch (error) {
      console.log(error);
      return {lat: 0, lon: 0};
    };
  }, [bufferZoneCoordinates]);
  
  // const [minColorValue, maxColorValue] = getMetricMinMax(metric);
  const [metricMinMax, setMetricMinMax] = useState(getMetricMinMax(metric));

  // when metric changes, reset min max
  useEffect(() => {
    setMetricMinMax(getMetricMinMax(metric));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metric]);

  const stagePolygonsCoordinates = useMemo(() => {
    try {

      const allPolygonsCoordinates = {};

      for (const stageName in clusterCoordinates3D) {
        
        const stageClusters = Object.keys(clusterCoordinates3D?.[stageName]);
        const firstCluster = stageClusters?.[0];
        const lastCluster = stageClusters?.[stageClusters?.length - 1];

        if (firstCluster !== lastCluster) { // if multi-cluster stage
          // Top start, top end, bottom end, bottom start (swap lon and lat)
          const stagePolygonCoordinates = [
            [clusterCoordinates3D?.[stageName]?.[firstCluster]?.center?.start?.lat, clusterCoordinates3D?.[stageName]?.[firstCluster]?.center?.start?.lon],
            [clusterCoordinates3D?.[stageName]?.[firstCluster]?.center?.end?.lat, clusterCoordinates3D?.[stageName]?.[firstCluster]?.center?.end?.lon],
            [clusterCoordinates3D?.[stageName]?.[lastCluster]?.center?.end?.lat, clusterCoordinates3D?.[stageName]?.[lastCluster]?.center?.end?.lon],
            [clusterCoordinates3D?.[stageName]?.[lastCluster]?.center?.start?.lat, clusterCoordinates3D?.[stageName]?.[lastCluster]?.center?.start?.lon]
          ];
          allPolygonsCoordinates[stageName] = stagePolygonCoordinates;
        } else { // if single-cluster stage
          const trimmedStageNumber = String(Number(String(stageName?.split('_')?.[2])?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')?.trim()));
          const wellName = stageName?.split('_')?.[1]?.toLowerCase();
          const lastStageNumber = Object.keys(currentRTFO?.perfSchedule?.wells?.[wellName]?.stages)?.slice(-1)?.[0];
          const currentStageDepth = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[trimmedStageNumber]?.clusters?.['1']?.shotDepth;
          const surveyData = currentRTFO?.surveys?.wells?.[wellName]?.surveyData;
          const wellLatLngs = wellCoordinates[wellName]?.map(c => [c.lat, c.lon]);

          let stageLength = 0;

          if (trimmedStageNumber !== lastStageNumber) {
            const nextStageDepth = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[Number(trimmedStageNumber) + 1]?.clusters?.['1']?.shotDepth;
            stageLength = currentStageDepth - nextStageDepth;
          } else { // for the last stage
            const previousStageDepth = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[Number(trimmedStageNumber) - 1]?.clusters?.['1']?.shotDepth;
            stageLength = previousStageDepth - currentStageDepth;
          };

          const fracLength = ((currentRTFO?.stageData?.[stageName]?.fracModel?.halfLength * 2) || (currentRTFO?.stageData?.[stageName]?.fracModel?.fracArea / (currentRTFO?.settings?.fracHeight)) || 600) * fracLengthDisplay;
          
          if (stageLength > 0) {
            // For bottom pseudo-cluster line
            const firstClusterLatLon = interpolateCoordinates(surveyData, wellLatLngs, currentStageDepth - stageLength / 3);
            let firstClusterLineCoordinates = calculateLineCoordinates(firstClusterLatLon, fracLength);
            firstClusterLineCoordinates = rotatePolyline(firstClusterLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));
  
            // For top pseudo-cluster line
            const lastClusterLatLon = interpolateCoordinates(surveyData, wellLatLngs, currentStageDepth + stageLength / 3);
            let lastClusterLineCoordinates = calculateLineCoordinates(lastClusterLatLon, fracLength);
            lastClusterLineCoordinates = rotatePolyline(lastClusterLineCoordinates, (currentRTFO?.settings?.fracAngle || 45));
  
            const stagePolygonCoordinates = [
              [firstClusterLineCoordinates?.[0]?.[0], firstClusterLineCoordinates?.[0]?.[1]],
              [firstClusterLineCoordinates?.[2]?.[0], firstClusterLineCoordinates?.[2]?.[1]],
              [lastClusterLineCoordinates?.[2]?.[0], lastClusterLineCoordinates?.[2]?.[1]],
              [lastClusterLineCoordinates?.[0]?.[0], lastClusterLineCoordinates?.[0]?.[1]]
            ];
            allPolygonsCoordinates[stageName] = stagePolygonCoordinates;
          };

        };

      };

      return allPolygonsCoordinates;

    } catch(error) {
      console.log(error);
      return null;
    };
  }, [clusterCoordinates3D, wellCoordinates, currentRTFO?.perfSchedule?.wells, currentRTFO?.surveys?.wells, currentRTFO?.stageData, currentRTFO?.settings?.fracHeight, currentRTFO?.settings?.fracAngle, fracLengthDisplay]);

  const allStagePolygonsCoordinatesFromAllPads = useMemo(() => {
    try {
      const allPolygonsCoordinatesFromAllPads = {}
      for (const padName of Object.keys(allPadsClusterCoordinates3D)) {
        let padPolygonsCoords = {}

        for (const stageName of Object.keys(
          allPadsClusterCoordinates3D[padName],
        )) {
          // const padKey = stageName?.split('_')?.[0]
          const allStageCluster = Object.keys(
            allPadsClusterCoordinates3D?.[padName]?.[stageName],
          )
          const firstCluster = allStageCluster?.[0]
          const lastCluster = allStageCluster?.[allStageCluster?.length - 1]

          if (firstCluster !== lastCluster) {
            // if multi-cluster stage
            // Top start, top end, bottom end, bottom start (swap lon and lat)
            const stagePolygonCoordinates = [
              [
                allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                  firstCluster
                ]?.center?.start?.lat,
                allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                  firstCluster
                ]?.center?.start?.lon,
              ],
              [
                allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                  firstCluster
                ]?.center?.end?.lat,
                allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                  firstCluster
                ]?.center?.end?.lon,
              ],
              [
                allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                  lastCluster
                ]?.center?.end?.lat,
                allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                  lastCluster
                ]?.center?.end?.lon,
              ],
              [
                allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                  lastCluster
                ]?.center?.start?.lat,
                allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                  lastCluster
                ]?.center?.start?.lon,
              ],
            ]
            padPolygonsCoords[stageName] = stagePolygonCoordinates
          } else {
            // if single-cluster stage
            const trimmedStageNumber = String(
              Number(
                String(stageName?.split('_')?.[2])
                  ?.replace(/[^0-9.]+|^0(?=[1-9])/g, '')
                  ?.trim(),
              ),
            )
            const wellName = stageName?.split('_')?.[1]?.toLowerCase()
            const lastStageNumber = Object.keys(
              filteredAllPadsMetrics[padName]?.perfSchedule?.wells?.[wellName]?.stages,
            )?.slice(-1)?.[0]
            const currentStageDepth =
              filteredAllPadsMetrics[padName]?.perfSchedule?.wells?.[wellName]
                ?.stages?.[trimmedStageNumber]?.clusters?.['1']?.shotDepth
            const surveyData =
              filteredAllPadsMetrics[padName]?.surveys?.wells?.[wellName]?.surveyData
            const wellLatLngs = allPadsWellCoordinates?.[padName]?.[
              wellName
            ]?.map((c) => [c.lat, c.lon])

            let stageLength = 0

            if (trimmedStageNumber !== lastStageNumber) {
              const nextStageDepth =
                filteredAllPadsMetrics[padName]?.perfSchedule?.wells?.[wellName]
                  ?.stages?.[Number(trimmedStageNumber) + 1]?.clusters?.['1']
                  ?.shotDepth
              stageLength = currentStageDepth - nextStageDepth
            } else {
              // for the last stage
              const previousStageDepth =
                filteredAllPadsMetrics[padName]?.perfSchedule?.wells?.[wellName]
                  ?.stages?.[Number(trimmedStageNumber) - 1]?.clusters?.['1']
                  ?.shotDepth
              stageLength = previousStageDepth - currentStageDepth
            }

            const fracLength = (
              (filteredAllPadsMetrics[padName]?.metrics?.[stageName]?.halfLength * 2) ||
              (filteredAllPadsMetrics[padName]?.metrics?.[stageName]?.fracArea / filteredAllPadsMetrics[padName]?.settings?.fracHeight) || 600) *
              fracLengthDisplay

            if (stageLength > 0) {
              // For bottom pseudo-cluster line
              const firstClusterLatLon = interpolateCoordinates(
                surveyData,
                wellLatLngs,
                currentStageDepth - stageLength / 3,
              )
              let firstClusterLineCoordinates = calculateLineCoordinates(
                firstClusterLatLon,
                fracLength,
              )
              firstClusterLineCoordinates = rotatePolyline(
                firstClusterLineCoordinates,
                filteredAllPadsMetrics[padName]?.settings?.fracAngle || 45,
              )

              // For top pseudo-cluster line
              const lastClusterLatLon = interpolateCoordinates(
                surveyData,
                wellLatLngs,
                currentStageDepth + stageLength / 3,
              )
              let lastClusterLineCoordinates = calculateLineCoordinates(
                lastClusterLatLon,
                fracLength,
              )
              lastClusterLineCoordinates = rotatePolyline(
                lastClusterLineCoordinates,
                filteredAllPadsMetrics[padName]?.settings?.fracAngle || 45,
              )

              const stagePolygonCoordinates = [
                [
                  firstClusterLineCoordinates?.[0]?.[0],
                  firstClusterLineCoordinates?.[0]?.[1],
                ],
                [
                  firstClusterLineCoordinates?.[2]?.[0],
                  firstClusterLineCoordinates?.[2]?.[1],
                ],
                [
                  lastClusterLineCoordinates?.[2]?.[0],
                  lastClusterLineCoordinates?.[2]?.[1],
                ],
                [
                  lastClusterLineCoordinates?.[0]?.[0],
                  lastClusterLineCoordinates?.[0]?.[1],
                ],
              ]
              padPolygonsCoords[stageName] = stagePolygonCoordinates
            }
          }
        }

        allPolygonsCoordinatesFromAllPads[padName] = padPolygonsCoords
      }

      return allPolygonsCoordinatesFromAllPads
    } catch (error) {
      console.log(error)
      return null
    }
  }, [
    filteredAllPadsMetrics,
    allPadsClusterCoordinates3D,
    allPadsWellCoordinates,
    fracLengthDisplay,
  ])

  const stageColors = useMemo(() => {
    try {
      
      const allStagesColors = {};

      for (const stageName in stagePolygonsCoordinates) {
        const value = getMetricValue(stageName, metric, stressShadow, currentRTFO?.stageData, currentRTFO?.settings, currentRTFO?.perfSchedule, fracLengthDisplay, currentRTFO?.stages, currentRTFO?.monitorEvents, currentRTFO?.fiberData);
        
        let color = '';
        
        if (metric === 'Seismicity Risk') {
          if (parseInt(value) === 0 || !value) {
            color = 'white';
          } else if (parseInt(value) === 1) {
            color = 'yellow';
          } else if (parseInt(value) === 2) {
            color = 'peachpuff';
          } else if (parseInt(value) === 3) {
            color = 'gold';
          } else if (parseInt(value) === 4) {
            color = 'red';
          } else if (parseInt(value) === -1) {
            color = 'purple';
          } else if (parseInt(value) === -2) {
            color = 'grey';
          };
        } else if (currentRTFO?.stageData?.[stageName]) {
          const colorScale = scaleSequential(interpolateTurbo).domain(metricMinMax);
          color = colorScale(value);
          if (isMetricsMap && !dailyStages?.includes(stageName)) {
            color = 'grey';
          };
        };

        allStagesColors[stageName] = color;
      };
      
      return allStagesColors;
    } catch(error) {
      console.log(error);
      return '';
    };
  }, [stagePolygonsCoordinates, metric, stressShadow, currentRTFO?.stageData, currentRTFO?.settings, currentRTFO?.perfSchedule, currentRTFO?.stages, currentRTFO?.monitorEvents, currentRTFO?.fiberData, fracLengthDisplay, metricMinMax, isMetricsMap, dailyStages]);

  const stageColorsFromAllPads = useMemo(() => {
    try {
      const allStagesColors = {}

      for (const padName of Object.keys(
        allStagePolygonsCoordinatesFromAllPads,
      )) {
        let padStagesColors = {}
        for (const stageName in allStagePolygonsCoordinatesFromAllPads?.[
          padName
        ]) {
          const value = getMetricValue(
            stageName,
            metric,
            allPadsStressShadow?.[padName],
            filteredAllPadsMetrics?.[padName]?.metrics?.[stageName],
            filteredAllPadsMetrics?.[padName]?.settings,
            filteredAllPadsMetrics?.[padName]?.perfSchedule,
            fracLengthDisplay,
            filteredAllPadsMetrics?.[padName]?.stages,
            filteredAllPadsMetrics?.[padName]?.monitorEvents,
            filteredAllPadsMetrics?.[padName]?.fiberData
          )

          let color = ''

          if (metric === 'Seismicity Risk') {
            if (parseInt(value) === 0) {
              color = 'white'
            } else if (parseInt(value) === 1) {
              color = 'yellow'
            } else if (parseInt(value) === 2) {
              color = 'peachpuff'
            } else if (parseInt(value) === 3) {
              color = 'gold'
            } else if (parseInt(value) === 4) {
              color = 'red'
            } else if (parseInt(value) === 5) {
              color = 'purple'
            } else if (parseInt(value) === 6) {
              color = 'grey'
            }
          } else if (allPadsClusterCoordinates3D?.[padName]?.[stageName]) {
            const colorScale = scaleSequential(interpolateTurbo).domain(metricMinMax)
            color = colorScale(value)
          }

          padStagesColors[stageName] = color
        }

        allStagesColors[padName] = padStagesColors
      }

      return allStagesColors
    } catch (error) {
      console.log(error)
      return ''
    }
  }, [allStagePolygonsCoordinatesFromAllPads, metric, allPadsStressShadow, filteredAllPadsMetrics, fracLengthDisplay, allPadsClusterCoordinates3D, metricMinMax])

  const seismicData = useMemo(() => {
    if (!currentRTFO?.seismicData || !currentRTFO?.seismicData?.realtime) return [];

    let allEvents = [...(currentRTFO?.seismicData?.reprocessed || [])];
    for (const event of currentRTFO?.seismicData?.realtime || []) {
      if (!allEvents?.find(e => e.id === event.id)) {
        allEvents.push(event);
      }
    }

    let sortedEvents = [
      ...(seismicDataType === 'highPrecision'
        ? (currentRTFO?.seismicData?.realtime || []).filter(e => e.type === 'd')
        : seismicDataType === 'realtime'
          ? allEvents.filter(e => e.type !== 'd')
          : seismicDataType === 'reprocessed'
            ? allEvents.filter(e => e.type !== 'a' && e.type !== 'd')
            : [])
    ];

    sortedEvents = sortedEvents?.filter(e => e && Object.keys(e)?.length > 0) || [];
    sortedEvents?.sort((a, b) => b.time - a.time);

    if (isMetricsMap) {
      let startTimestamp = new Date(reportDate);
      startTimestamp.setHours(6);
      startTimestamp.setMinutes(0);
      startTimestamp.setSeconds(0);
      startTimestamp.setMilliseconds(0);
      startTimestamp = startTimestamp.getTime();

      const endTimestamp = startTimestamp + 24 * 60 * 60 * 1000;
      sortedEvents = sortedEvents.filter(e => e?.time >= startTimestamp && e?.time < endTimestamp);
    }

    // filter out events with magnitude < 5
    sortedEvents = sortedEvents.filter(e => e?.localMagnitude > -5);


    // if map features are present, filter events outside
    if (currentRTFO?.mapFeatures?.length > 0) {
      const combinedEvents = [];

      for (const mapFeature of currentRTFO.mapFeatures) {
        if (!mapFeature?.isActive) continue;

        let polygonPoints = mapFeature?.layer?.latlngs?.flat(Infinity);
        polygonPoints = polygonPoints.map(p => [p.lng, p.lat]);

        // Ensure the polygon is closed by adding the first coordinate at the end if it's not already
        if (
          polygonPoints[0][0] !== polygonPoints[polygonPoints.length - 1][0] ||
          polygonPoints[0][1] !== polygonPoints[polygonPoints.length - 1][1]
        ) {
          polygonPoints.push(polygonPoints[0]);
        }

        const polygon = turf.polygon([polygonPoints]);
        const featureEvents = sortedEvents.filter(e => {
          const point = turf.point([e.longitude, e.latitude]);
          return turf.booleanPointInPolygon(point, polygon);
        });

        featureEvents?.forEach(e => {
          if (!combinedEvents.find(_e => _e.id === e.id)) {
            combinedEvents.push(e);
          }
        });
      }

      if (combinedEvents?.length > 0) {
        return combinedEvents;
      }
    }
    
    return sortedEvents;

  }, [currentRTFO.mapFeatures, currentRTFO?.seismicData, isMetricsMap, reportDate, seismicDataType]);


  try {
    return (
      <Paper style={{margin: '0 20px', padding: 0, backgroundColor: theme?.palette?.elevationOne?.[(darkMode ? 'dark' : 'light')], position: 'relative'}} elevation={0} sx={{boxShadow: 1}}>
  
        <div style={{ position: 'absolute', left: 10, top: 197, zIndex: 999, height: 32, width: 32, backgroundColor: '#fff', border: '2px solid rgb(191,191,191)', borderRadius: '4px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <IconButton
            color={tilesType === 'map' || darkMode ? '#333' : 'primary'}
            title="Toggle Satellite View"
            onClick={() =>
              setTilesType((prev) => (prev === 'map' ? 'satellite' : 'map'))
            }
            size="small"
          >
            <Satellite style={{ height: 22, width: 22, color: tilesType === 'map' ? 'black' : colors[0] }} />
            </IconButton>
        </div>

        <div
          sx={{ boxShadow: 1 }}
          style={{
            width: 'calc(100% - 300px)',
            height: 60,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            position: 'absolute',
            top: 0,
            left: 60,
            zIndex: 420,
            borderRadius: 4,
            // backgroundColor: !darkMode
            //   ? 'rgba(255, 255, 255, 0.8)'
            //   : 'transparent',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end',
            }}
          >
            

            <div style={{ display: 'flex', flexDirection: 'row' }}>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}
              >
                <Typography fontSize="12px">Metric</Typography>
                <FormControl>
                  <Select
                    value={metric}
                    onChange={(e) => setMetric(e.target.value)}
                    size="small"
                    style={{
                      width: 150,
                      height: 28,
                      backgroundColor: !darkMode && '#fff',
                      fontSize: '12px',
                    }}
                  >
                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Stage Information
                    </Typography>
                    {allMetrics['Stage Information']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />
                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Pumping Information
                    </Typography>
                    {allMetrics['Pumping Information']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      ISIP
                    </Typography>
                    {allMetrics['ISIP']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Stepdown Model
                    </Typography>
                    {allMetrics['Stepdown Model']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      Frac Model
                    </Typography>
                    {allMetrics['Frac Model']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />

                    <Typography
                      variant="caption"
                      style={{ marginLeft: 10 }}
                    >
                      FDI
                    </Typography>
                    {allMetrics['FDI']?.map((m) => (
                      <MenuItem
                        value={m}
                        key={m}
                      >
                        {m}
                      </MenuItem>
                    ))}
                    <Divider />
                  
                  </Select>
                </FormControl>
              </div>

              {!isMetricsMap && <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  marginLeft: 20,
                  position: 'relative',
                }}
              >
                <Typography fontSize="12px">Pads</Typography>

                {isLoadingPadsMetrics ? (
                  <CircularProgress style={{ width: 28, height: 28 }} />
                ) : (
                  <FormControl>
                    <Select
                      value={displayPads}
                      size="small"
                      multiple
                      renderValue={(selected) => selected.join(', ')}
                      style={{
                        width: 100,
                        height: 28,
                        backgroundColor: !darkMode && '#fff',
                        fontSize: '12px',
                      }}
                      disabled={isLoadingPadsMetrics}
                    >
                      <MenuItem
                        value={'selectAll'}
                        onClick={() => handlePadCheckClick('selectAll')}
                        style={{ height: 30 }}
                      >
                        <Checkbox checked={true} />
                        <ListItemText primary={'Select All'} />
                      </MenuItem>

                      <Divider />

                      <MenuItem
                        value={'deselectAll'}
                        onClick={() => handlePadCheckClick('deselectAll')}
                        style={{ height: 30 }}
                      >
                        <Checkbox checked={false} />
                        <ListItemText primary={'Deselect All'} />
                      </MenuItem>

                      <Divider />

                      {filteredAllPadsAreas && filteredAllPadsAreas?.map(area => {
                        const filteredPadNames = filteredAllPadsNames?.filter(padName => allPadsMetrics?.[padName]?.area === area);
                        filteredPadNames.sort((a, b) => new Date(allPadsMetrics?.[b]?.createdOn) - new Date(allPadsMetrics?.[a]?.createdOn))
                        return (
                          <div key={area}>
                            <Typography variant='caption' style={{marginLeft: 10}}>{area}</Typography>
                            {filteredPadNames &&
                              filteredPadNames?.map((padName, i) => (
                                <div key={padName}>
                                  <MenuItem
                                    value={padName}
                                    onClick={() => handlePadCheckClick(padName)}
                                    style={{ height: 28 }}
                                  >
                                    <Checkbox
                                      checked={displayPads.indexOf(padName) > -1}
                                    />
                                    <ListItemText primary={padName} />
                                  </MenuItem>
                                  <Divider />
                                </div>
                              ))}
                          </div>
                        )
                      })}
                    </Select>
                  </FormControl>
                )}
              </div>}

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  marginLeft: 20,
                }}
              >
                <Typography fontSize="12px">Seismicity Data</Typography>
                <FormControl>
                  <Select 
                    value={seismicDataType}
                    onChange={(e) => setSeismicDataType(e.target.value)}
                    size="small"
                    style={{width: 150, height: 28, backgroundColor: !darkMode && '#fff', fontSize: '12px'}}
                    disabled={!padHasSeismicityData}
                  >
                    <MenuItem value={'realtime'} disabled={!padHasSeismicityData}>Realtime</MenuItem>
                    <MenuItem value={'reprocessed'} disabled={!padHasSeismicityData}>Confirmed</MenuItem>
                    <MenuItem value={'highPrecision'} disabled={!padHasSeismicityData}>High Precision</MenuItem>
                    <MenuItem value={'off'}>Off</MenuItem>
                  </Select>
                </FormControl>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  marginLeft: 20,
                }}
              >
                <Typography fontSize="12px">Hydr. Half Length</Typography>
                <FormControl>
                  <Select
                    value={fracLengthDisplay}
                    onChange={(e) => setFracLengthDisplay(e.target.value)}
                    size="small"
                    style={{
                      width: 100,
                      height: 28,
                      backgroundColor: !darkMode && '#fff',
                      fontSize: '12px',
                    }}
                  >
                    <MenuItem value={1}>100%</MenuItem>
                    <MenuItem value={0.5}>50%</MenuItem>
                    <MenuItem value={0.25}>25%</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
            }}
          >
            <div>
              <Button
                startIcon={<FastRewind />}
                onClick={() => {
                  const firstIndex =
                    selectedWellName === 'All'
                      ? 0
                      : pumpedStages?.findIndex((stageName) =>
                          stageName.includes(selectedWellName),
                        )
                  dispatch(setCurrentStageName(pumpedStages?.[firstIndex]))
                }}
                color={darkMode ? 'inherit' : 'primary'}
                size="small"
                style={{
                  color: '#fff',
                  marginRight: 10,
                  height: 28,
                  fontSize: '12px',
                }}
                variant="contained"
              >
                First
              </Button>
              <Button
                startIcon={<SkipPrevious />}
                onClick={() => {
                  let currentIndex = pumpedStages?.indexOf(currentStageName)
                  if (currentIndex > 0) {
                    if (selectedWellName !== 'All') {
                      while (
                        currentIndex > 0 &&
                        !pumpedStages?.[currentIndex - 1]?.includes(
                          selectedWellName,
                        )
                      ) {
                        currentIndex--
                      }
                    }
                    dispatch(
                      setCurrentStageName(pumpedStages?.[currentIndex - 1]),
                    )
                  }
                }}
                color={darkMode ? 'inherit' : 'primary'}
                size="small"
                style={{
                  color: '#fff',
                  marginRight: 10,
                  height: 28,
                  fontSize: '12px',
                }}
                variant="contained"
              >
                Previous
              </Button>
              <Button
                startIcon={isPlayblast ? <Stop /> : <PlayArrow />}
                onClick={() => setIsPlayblast((prev) => !prev)}
                color={darkMode ? 'inherit' : 'primary'}
                size="small"
                style={{
                  color: '#fff',
                  marginRight: 10,
                  height: 28,
                  fontSize: '12px',
                }}
                variant="contained"
              >
                {isPlayblast ? 'Stop' : 'Start'}
              </Button>
              <Button
                startIcon={<SkipNext />}
                onClick={() => {
                  let currentIndex = pumpedStages?.indexOf(currentStageName)
                  if (
                    currentIndex >= 0 &&
                    currentIndex < pumpedStages?.length - 1
                  ) {
                    if (selectedWellName !== 'All') {
                      while (
                        currentIndex > 0 &&
                        currentIndex < pumpedStages?.length - 1 &&
                        !pumpedStages?.[currentIndex + 1]?.includes(
                          selectedWellName,
                        )
                      ) {
                        currentIndex++
                      }
                    }
                    dispatch(
                      setCurrentStageName(pumpedStages?.[currentIndex + 1]),
                    )
                  }
                }}
                color={darkMode ? 'inherit' : 'primary'}
                size="small"
                style={{
                  color: '#fff',
                  marginRight: 10,
                  height: 28,
                  fontSize: '12px',
                }}
                variant="contained"
              >
                Next
              </Button>
              <Button
                startIcon={<FastForward />}
                onClick={() => {
                  const lastIndex =
                    selectedWellName === 'All'
                      ? pumpedStages?.length - 1
                      : pumpedStages?.findLastIndex((stageName) =>
                          stageName.includes(selectedWellName),
                        )
                  dispatch(setCurrentStageName(pumpedStages?.[lastIndex]))
                }}
                color={darkMode ? 'inherit' : 'primary'}
                size="small"
                style={{
                  color: '#fff',
                  marginRight: 10,
                  height: 28,
                  fontSize: '12px',
                }}
                variant="contained"
              >
                Last
              </Button>
            </div>
          </div>
        </div>

        <div style={{ borderRadius: '5px', height: 900, overflow: 'hidden', position: 'relative' }}>
          <MapContainer
            center={[globalCenter?.lat, globalCenter?.lon]}
            zoom={14}
            maxZoom={16}
            ref={mapRef}
            style={{ height: 900 }}
            attributionControl={true}
            zoomControl={true}
            scrollWheelZoom={false}
          > 
            <LayersControl position="topright">
            <BaseLayer checked name="Base Map">
              {tilesType === 'satellite' ? 
              <TileLayer
                url={`https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}`}
                removeAttribution
                attributionControl="false"
              />
              : darkMode ? 
                <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Dark_Gray_Base/MapServer/tile/{z}/{y}/{x}`}  /> :
                <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}`}  />}    
            </BaseLayer>
            <Overlay checked name="Boundaries">
              <TileLayer url={`https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places_Alternate/MapServer/tile/{z}/{y}/{x}`} />
            </Overlay>
          </LayersControl>

            {bufferZoneCoordinates && !isBufferZoneHidden && (
              <Polygon
                positions={bufferZoneCoordinates}
                pathOptions={{
                  fillOpacity: 0.75,
                  color: 'red',
                  fillColor: 'transparent',
                }}
              />
            )}

            {fpzCoordinates && !isFPZHidden && (
              <Polygon
                positions={fpzCoordinates}
                pathOptions={{
                  fillOpacity: 0.25,
                  color: 'transparent',
                  fillColor: 'grey',
                }}
              />
            )}

            {!isFPZHidden &&
              Object.keys(allPadsFpzCoordinates)?.map((fpzKey, index) => {
                return (
                  <Polygon
                    key={`${index}-${fpzKey}`}
                    positions={allPadsFpzCoordinates[fpzKey]}
                    pathOptions={{
                      fillOpacity: 0.25,
                      color: 'transparent',
                      fillColor: 'grey',
                    }}
                  />
                )
              })}

            {Object.keys(wellCoordinates)?.map((well, index) => {
              return (
                <div key={`${well}-${index}`}>
                  <Polyline
                    pathOptions={{
                      color:
                        well ===
                        currentStageName?.split('_')?.[1]?.toLowerCase()
                          ? 'red'
                          : 'black',
                      weight: 6,
                    }}
                    positions={wellCoordinates[well]?.map((w) => [
                      w.lat,
                      w.lon,
                    ])}
                    bounds={L.latLngBounds(
                      wellCoordinates[well]?.map((w) => [w.lat, w.lon]),
                    )}
                  />
                  <CircleMarker
                    center={[
                      currentRTFO?.surveys?.wells?.[well]?.surfaceLat,
                      currentRTFO?.surveys?.wells?.[well]?.surfaceLon,
                    ]}
                    pathOptions={{ color: 'black' }}
                    radius={10}
                  ></CircleMarker>
                  <WellLabel
                    center={[
                      wellCoordinates[well]?.[wellCoordinates[well]?.length - 1]
                        ?.lon,
                      wellCoordinates[well]?.[wellCoordinates[well]?.length - 1]
                        ?.lat,
                    ]}
                    wellName={well}
                    currentWellName={currentStageName
                      ?.split('_')?.[1]
                      ?.toLowerCase()}
                    wellAlias={currentRTFO?.wellAlias?.[well] || ''}
                  />
                  {index === 0 && (
                    <PadLabel
                      center={[
                        currentRTFO?.surveys?.wells?.[well]
                          ?.surfaceLon,
                        currentRTFO?.surveys?.wells?.[well]
                          ?.surfaceLat,
                      ]}
                      padName={currentRTFO?.name}
                    />
                  )}
                </div>
              )
            })}

            {allPadsWellCoordinates &&
              Object.keys(allPadsWellCoordinates)?.map((padName) => {
                return Object.keys(allPadsWellCoordinates[padName])?.map(
                  (well, index) => {
                    return (
                      <div key={`${well}-${index}`}>
                        <Polyline
                          pathOptions={{
                            color: 'grey',
                            weight: 6,
                          }}
                          positions={allPadsWellCoordinates?.[padName]?.[
                            well
                          ]?.map((w) => [w.lat, w.lon])}
                          bounds={L.latLngBounds(
                            allPadsWellCoordinates?.[padName]?.[well]?.map(
                              (w) => [w.lat, w.lon],
                            ),
                          )}
                        />
                        <CircleMarker
                          center={[
                            filteredAllPadsMetrics?.[padName]?.surveys?.wells?.[well]
                              ?.surfaceLat,
                            filteredAllPadsMetrics?.[padName]?.surveys?.wells?.[well]
                              ?.surfaceLon,
                          ]}
                          pathOptions={{ color: 'grey' }}
                          radius={10}
                        ></CircleMarker>
                        <WellLabel
                          center={[
                            allPadsWellCoordinates?.[padName]?.[well]?.slice(
                              -1,
                            )?.[0]?.lon,
                            allPadsWellCoordinates?.[padName]?.[well]?.slice(
                              -1,
                            )?.[0]?.lat,
                          ]}
                          wellName={well}
                          currentWellName={''}
                          wellAlias={''}
                        />
                        {index === 0 && (
                          <PadLabel
                            center={[
                              filteredAllPadsMetrics?.[padName]?.surveys?.wells?.[well]
                                ?.surfaceLon,
                              filteredAllPadsMetrics?.[padName]?.surveys?.wells?.[well]
                                ?.surfaceLat,
                            ]}
                            padName={padName}
                          />
                        )}
                      </div>
                    )
                  },
                )
              })}

            {Object.keys(stagePolygonsCoordinates)?.map((stageName) => {
              return stagePolygonsCoordinates[stageName] &&
                (metric === 'Seismicity Risk' ||
                  currentRTFO?.stageData?.[stageName]) &&
                (selectedWellName === 'All' ||
                  stageName?.includes(selectedWellName)) ? (
                <Polygon
                  positions={stagePolygonsCoordinates[stageName]}
                  pathOptions={{
                    fillColor: stageColors?.[stageName],
                    fillOpacity: 0.75,
                    color: 'transparent',
                  }}
                  key={stageName}
                />
              ) : (
                <div key={stageName}></div>
              )
            })}

            {allStagePolygonsCoordinatesFromAllPads &&
              Object.keys(allStagePolygonsCoordinatesFromAllPads)?.map(
                (padName) => {
                  return Object.keys(
                    allStagePolygonsCoordinatesFromAllPads[padName],
                  )?.map((stageName) => {
                    return (
                      <Polygon
                        positions={
                          allStagePolygonsCoordinatesFromAllPads?.[padName]?.[
                            stageName
                          ]
                        }
                        pathOptions={{
                          fillColor:
                            stageColorsFromAllPads?.[padName]?.[stageName],
                          fillOpacity: 0.75,
                          color: 'transparent',
                        }}
                        key={stageName}
                      />
                    )
                  })
                },
              )}

            {allStages?.map((stageName) => {
              return stageCoordinates3D[stageName] &&
                (selectedWellName === 'All' ||
                  stageName?.includes(selectedWellName)) ? (
                <StageLabel
                  key={stageName}
                  coord={stageCoordinates3D[stageName]?.center}
                  value={getMetricValue(
                    stageName,
                    metric,
                    stressShadow,
                    currentRTFO?.stageData,
                    currentRTFO?.settings,
                    currentRTFO?.perfSchedule,
                    fracLengthDisplay,
                    currentRTFO?.stages,
                    currentRTFO?.monitorEvents,
                    currentRTFO?.fiberData
                  )}
                  metric={metric}
                  stageName={stageName}
                />
              ) : (
                <div key={stageName}></div>
              )
            })}

            {allPadsStages &&
              Object.keys(allPadsStages)?.map((padName) => {
                return allPadsStages?.[padName]?.map((stageName) => {
                  return (
                    allPadsStageCoordinates3D?.[padName]?.[stageName] && (
                      <StageLabel
                        key={stageName}
                        coord={
                          allPadsStageCoordinates3D?.[padName]?.[stageName]
                            ?.center
                        }
                        value={getMetricValue(
                          stageName,
                          metric,
                          allPadsStressShadow?.[padName],
                          filteredAllPadsMetrics?.[padName]?.metrics?.[stageName],
                          filteredAllPadsMetrics?.[padName]?.settings,
                          filteredAllPadsMetrics?.[padName]?.perfSchedule,
                          fracLengthDisplay,
                          filteredAllPadsMetrics?.[padName]?.stages,
                          filteredAllPadsMetrics?.[padName]?.monitorEvents,
                          filteredAllPadsMetrics?.[padName]?.fiberData
                        )}
                        metric={metric}
                        stageName={stageName}
                      />
                    )
                  )
                })
              })}

            {!isNaN(stageIntersectionLineCoordinates?.[0]?.[0]) &&
            !isNaN(stageIntersectionLineCoordinates?.[1]?.[0]) ? (
              <Polyline
                pathOptions={{ color: chartColors[0], weight: 2 }}
                positions={stageIntersectionLineCoordinates}
                bounds={L.latLngBounds(stageIntersectionLineCoordinates)}
              ></Polyline>
            ) : (
              <></>
            )}

            {stageCoordinates3D &&
              Object.keys(stageCoordinates3D)?.map((stageName) =>
                selectedWellName === 'All' ||
                stageName?.includes(selectedWellName) ? (
                  Object.keys(clusterCoordinates3D[stageName])?.map(
                    (cluster, cIndex) => {
                      const clusterLineCoordinates = [
                        [
                          clusterCoordinates3D[stageName]?.[cluster]?.center
                            ?.start?.lat,
                          clusterCoordinates3D[stageName]?.[cluster]?.center
                            ?.start?.lon,
                        ],
                        [
                          clusterCoordinates3D[stageName]?.[cluster]?.center
                            ?.center?.lat,
                          clusterCoordinates3D[stageName]?.[cluster]?.center
                            ?.center?.lon,
                        ],
                        [
                          clusterCoordinates3D[stageName]?.[cluster]?.center
                            ?.end?.lat,
                          clusterCoordinates3D[stageName]?.[cluster]?.center
                            ?.end?.lon,
                        ],
                      ]
                      return !isNaN(clusterLineCoordinates?.[0]?.[0]) &&
                        !isNaN(clusterLineCoordinates?.[1]?.[0]) &&
                        !isNaN(clusterLineCoordinates?.[2]?.[0]) ? (
                        <Polyline
                          pathOptions={{
                            weight: stageName === currentStageName ? 2 : 1,
                            color:
                              stageName === currentStageName
                                ? 'black'
                                : currentRTFO?.stageData?.[stageName]
                                  ? stageColors?.[stageName]
                                  : 'grey',
                            lineCap: 'butt',
                          }}
                          positions={clusterLineCoordinates}
                          bounds={L.latLngBounds(clusterLineCoordinates)}
                          key={`${stageName}-${cluster}-${cIndex}`}
                        >
                          {/* <Polyline pathOptions={{ weight: stageName === currentStageName ? 2 : 1, color: stageName === currentStageName ? 'red' : currentRTFO?.stageData?.[stageName] ? 'black' : 'grey', lineCap: 'butt' }} positions={clusterLineCoordinates} bounds={L.latLngBounds(clusterLineCoordinates)} key={`${stageName}-${cluster}-${cIndex}`}> */}
                          <Tooltip
                            direction="bottom"
                            offset={[0, 0]}
                            opacity={1}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                              }}
                            >
                              <Typography
                                textAlign={'center'}
                                fontSize="10px"
                              >{`${stageName?.split('_')?.[1]} - ${stageName?.split('_')?.[2]}`}</Typography>
                              <Typography
                                textAlign={'center'}
                                fontSize="10px"
                              >
                                {getMetricValue(
                                  stageName,
                                  metric,
                                  stressShadow,
                                  currentRTFO?.stageData,
                                  currentRTFO?.settings,
                                  currentRTFO?.perfSchedule,
                                  fracLengthDisplay,
                                  currentRTFO?.stages,
                                  currentRTFO?.monitorEvents, 
                                  currentRTFO?.fiberData
                                )?.toFixed(2)}{' '}
                                {getMetricUnits(getMetricParamName(metric))}
                              </Typography>
                            </div>
                          </Tooltip>
                        </Polyline>
                      ) : (
                        <div key={cIndex}></div>
                      )
                    },
                  )
                ) : (
                  <div key={stageName}></div>
                ),
              )}

            {allPadsStageCoordinates3D &&
              Object.keys(allPadsStageCoordinates3D)?.map((padName) => {
                return Object.keys(allPadsStageCoordinates3D?.[padName])?.map(
                  (stageName) =>
                    Object.keys(
                      allPadsClusterCoordinates3D?.[padName]?.[stageName],
                    )?.map((cluster, cIndex) => {
                      const clusterLineCoordinates = [
                        [
                          allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                            cluster
                          ]?.center?.start?.lat,
                          allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                            cluster
                          ]?.center?.start?.lon,
                        ],
                        [
                          allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                            cluster
                          ]?.center?.center?.lat,
                          allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                            cluster
                          ]?.center?.center?.lon,
                        ],
                        [
                          allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                            cluster
                          ]?.center?.end?.lat,
                          allPadsClusterCoordinates3D?.[padName]?.[stageName]?.[
                            cluster
                          ]?.center?.end?.lon,
                        ],
                      ]

                      return !isNaN(clusterLineCoordinates?.[0]?.[0]) &&
                        !isNaN(clusterLineCoordinates?.[1]?.[0]) &&
                        !isNaN(clusterLineCoordinates?.[2]?.[0]) ? (
                        <Polyline
                          pathOptions={{
                            weight: 1,
                            color:
                              stageColorsFromAllPads?.[padName]?.[stageName] ||
                              'grey',
                            lineCap: 'butt',
                          }}
                          positions={clusterLineCoordinates}
                          bounds={L.latLngBounds(clusterLineCoordinates)}
                          key={`${stageName}-${cluster}-${cIndex}`}
                        ></Polyline>
                      ) : (
                        <div key={cIndex}></div>
                      )
                    }),
                )
              })}

            {seismicData?.map((event, i) => {
              const stageNames = currentRTFO?.stages ? currentRTFO?.stages : [];
              const activeStageName = stageNames?.find((stageName,i) => 
                event?.time >= currentRTFO?.stageData?.[stageName]?.stageStartTimestamp &&
                (i === stageNames?.length - 1 || event?.time <= currentRTFO?.stageData?.[stageNames?.[i + 1]]?.stageStartTimestamp)
              );
              const activeStageIndex = stageNames?.indexOf(activeStageName);
              const currentStageIndex = stageNames?.indexOf(currentStageName);

              let isEventFilteredOut = false;

              if (seismicFilter?.length > 0) {
                if (
                  seismicFilter?.includes(0.75) &&
                  event?.localMagnitude < 1
                ) {
                  isEventFilteredOut = true
                } else if (
                  seismicFilter?.includes(1.5) &&
                  event?.localMagnitude >= 1 &&
                  event?.localMagnitude < 2
                ) {
                  isEventFilteredOut = true
                } else if (
                  seismicFilter?.includes(2.5) &&
                  event?.localMagnitude >= 2 &&
                  event?.localMagnitude < 3
                ) {
                  isEventFilteredOut = true
                } else if (
                  seismicFilter?.includes(3.5) &&
                  event?.localMagnitude >= 3 &&
                  event?.localMagnitude < 4
                ) {
                  isEventFilteredOut = true
                } else if (
                  seismicFilter?.includes(4.5) &&
                  event?.localMagnitude >= 4
                ) {
                  isEventFilteredOut = true
                }
              }

              if (
                !isEventFilteredOut &&
                activeStageName &&
                activeStageIndex <= currentStageIndex &&
                (selectedWellName === 'All' ||
                  activeStageName?.includes(selectedWellName))
              ) {
                // if (!isEventFilteredOut && activeStageName && distance > 0 && distance <= 10 && activeStageIndex <= currentStageIndex && (selectedWellName === 'All' || activeStageName?.includes(selectedWellName))) {
                return (
                  <CircleMarker
                    center={[event?.latitude, event?.longitude]}
                    pane={'markerPane'}
                    pathOptions={{
                      weight: 1,
                      color: 'black',
                      fillColor: getSeismicEventColor(event?.localMagnitude),
                      fillOpacity: 0.75,
                    }}
                    radius={getSeismicEventSize(event?.localMagnitude)}
                    key={`${event?.id}-${event?.type}-${i}`}
                  >
                    <Tooltip
                      direction="bottom"
                      offset={[0, 10]}
                      opacity={1}
                    >
                      <Typography>
                        Date: {new Date(event?.time)?.toLocaleString()}
                      </Typography>
                      <Typography>
                        Active stage: {activeStageName || 'n/a'}
                      </Typography>
                      <Typography>
                        Distance: {event?.distance?.toFixed(2)} km
                      </Typography>
                      <Typography>Magnitude: {event?.magnitude}</Typography>
                      <Typography>
                        Moment magnitude: {event?.momentMagnitude}
                      </Typography>
                      <Typography>
                        Local magnitude: {event?.localMagnitude}
                      </Typography>
                      <Typography>
                        Event type:{' '}
                        {event?.type === 'm' ? 'Confirmed' : 'Realtime'}
                      </Typography>
                    </Tooltip>
                  </CircleMarker>
                )
              } else {
                return <div key={`${event?.id}-${event?.type}-${i}`}></div>
              }
            })}

            <ScrollZoomControl setScrollZoomControlOverlayVisible={setScrollZoomControlOverlayVisible} />
            <RTFOMapMeasureControl />
            <RTFOMapDrawingControl currentRTFO={currentRTFO} />
            
            {scrollZoomControlOverlayVisible && 
          <ScrollZoomControlOverlay setScrollZoomControlOverlayVisible={setScrollZoomControlOverlayVisible}/>}
          
          </MapContainer>

          <RTFOMapFeatures currentRTFO={currentRTFO} />

          <RTFOMapLegend
            darkMode={darkMode}
            metricMinMax={metricMinMax}
            setMetricMinMax={setMetricMinMax}
            metric={metric}
            showMetric={true}
            showSeismic={currentRTFO?.seismicData?.[seismicDataType]}
            seismicFilter={seismicFilter}
            setSeismicFilter={setSeismicFilter}
            isBufferZoneHidden={isBufferZoneHidden}
            setIsBufferZoneHidden={setIsBufferZoneHidden}
            isFPZHidden={isFPZHidden}
            setIsFPZHidden={setIsFPZHidden}
          />


          

        </div>

        {currentRTFO?.seismicData?.[seismicDataType] && selectedWellName !== 'All' &&
        <div style={{ position: 'absolute', padding: 10, bottom: 545, left: '50%', transform: 'translateX(-50%)', zIndex: 420, backgroundColor: 'rgba(255, 255, 255, 0.5)'}}>
          <Typography>Showing seismicity events for well {selectedWellName}</Typography>
        </div>}


        {currentStageName && <div style={{ position: 'absolute', bottom: 540, left: 8, zIndex: 420}}>
          <Typography fontSize='12px' style={{ position: 'absolute', left: '50%', top: 5, transform: 'translateX(-50%)'}}>Cross-Section View</Typography>
          <WellboreCrossSection 
            currentStageName={currentStageName} 
            wellCoordinates={wellCoordinates} 
            stageCoordinates3D={stageCoordinates3D} 
            clusterCoordinates3D={clusterCoordinates3D} 
            stressShadow={stressShadow}
            stageData={currentRTFO?.stageData}
            settings={currentRTFO?.settings}
            surveyData={currentRTFO?.surveys?.wells?.[currentWellName?.toLowerCase()]?.surveyData}
            fracLengthDisplay={fracLengthDisplay}
            formation={currentRTFO?.formation}
            client={currentRTFO?.client}
            darkMode={darkMode}
          />
        </div>}

        <div style={{width: '100%', height: 540}}>
          <RTFOWellboreTrajectoryChart currentStageName={ currentRTFO?.currentStageName } />
        </div>

      </Paper>
    );
  } catch (error) {
    console.log(error);
    return <></>;
  };

  function getMetricMinMax(metric) {
    try {
      const values = []
      const stages = {}

      // add stages from current pad
      for (let stageName of pumpedStages) {
        const value = getMetricValue(
          stageName,
          metric,
          stressShadow,
          currentRTFO?.stageData,
          currentRTFO?.settings,
          currentRTFO?.perfSchedule,
          fracLengthDisplay,
          currentRTFO?.stages,
          currentRTFO?.monitorEvents,
          currentRTFO?.fiberData
        )
        if (value > 0) {
          values.push(value)
          stages[stageName] = value
        }
      }

      // add stages from all selected background pads
      for (const padName of displayPads) {
        for (let stageName of allPadsStages?.[padName] || []) {
          const value = getMetricValue(
            stageName,
            metric,
            allPadsStressShadow?.[padName],
            filteredAllPadsMetrics?.[padName]?.metrics?.[stageName],
            filteredAllPadsMetrics[padName]?.settings,
            filteredAllPadsMetrics[padName]?.perfSchedule,
            fracLengthDisplay,
            filteredAllPadsMetrics?.[padName]?.stages,
            filteredAllPadsMetrics?.[padName]?.monitorEvents,
            filteredAllPadsMetrics?.[padName]?.fiberData
          )
          if (value > 0) {
            values.push(value)
            stages[stageName] = value
          }
        }
      }

      const minValue = Math.min(...values) * 0.95 || 0
      const maxValue = Math.max(...values) * 1.05 || 1

      if (metric === 'Seismicity Risk') {
        return [0, 4]
      } else {
        return [minValue, maxValue]
      }
    } catch (error) {
      console.log(error)
      return 0
    }
  }
  

  function StageLabel({ coord, value, metric, stageName }) {
    try {
      const isCurrentStage = stageName === currentRTFO?.currentStageName;

      const center = [coord?.center?.lon, coord?.center?.lat];
      const radius = 60 / 1000;
      const options = {steps: 16, units: 'kilometers'};
      const circle = turf.circle(center, radius, options);
      let coords = circle?.geometry?.['coordinates']?.[0]?.map(c => [c?.[1], c?.[0]]);

      const width = 90;
      const height = 140;

      // Calculate x and y coordinates of the rectangle center
      const centerX = center?.[1];
      const centerY = center?.[0];

      // Calculate x and y coordinates of the top-left corner of the rectangle bounding box
      const x = centerX - width / 2;
      const y = centerY - height / 2;

      const wellName = stageName?.split('_')?.[1]?.toLowerCase();
      const stageNumber = stageName?.split('_')?.[2]?.replace(/[^0-9]+/g, '');
      const stageClusters = currentRTFO?.perfSchedule?.wells?.[wellName]?.stages?.[stageNumber]?.clusters;
      const clusterNames = stageClusters && Object.keys(stageClusters);
      const numClusters = clusterNames?.length || 0;

      return (
        <SVGOverlay
          bounds={L.latLngBounds(coords)}
          attributes={{
            viewBox: `${x} ${y} ${width} ${height}`
          }}
        >
          <text x={centerX} y={centerY - (numClusters < 3 ? 10 : 15)} textAnchor="middle" dominantBaseline="middle" strokeWidth="2px" paintOrder="stroke" stroke={isCurrentStage ? "black" : "white"} fill={isCurrentStage ? "red" : "black"} fontSize={isCurrentStage ? '48px' : numClusters < 3 ? '24px' : '32px'} fontWeight="bold">
            {stageName?.split('_')?.[2]}
          </text>
          <text x={centerX} y={centerY + (numClusters < 3 ? 10 : 15)} textAnchor="middle" dominantBaseline="middle" strokeWidth="2px" paintOrder="stroke" stroke={isCurrentStage ? "black" : "white"} fill={isCurrentStage ? "red" : "black"} fontSize={isCurrentStage ? '32px' : numClusters < 3 ? '18px' : '24px'} fontWeight="bold">
          {value?.toFixed(1)}
          </text>
        </SVGOverlay>
      );

    } catch (error) {
      return <></>
    };
    
  };

  //multipad
  function handlePadCheckClick(padName) {
    if (padName === 'selectAll') {
      let padNames = filteredAllPadsMetrics ? Object.keys(filteredAllPadsMetrics) : [];
      padNames = padNames.sort((a, b) => new Date(filteredAllPadsMetrics[b]?.createdOn) - new Date(filteredAllPadsMetrics[a]?.createdOn))
      setDisplayPads(padNames)
    } else if (padName === 'deselectAll') {
      setDisplayPads([])
    } else if (displayPads?.indexOf(padName) === -1) {
      setDisplayPads((prev) => [...prev, padName]?.sort((a, b) => new Date(filteredAllPadsMetrics[b]?.createdOn) - new Date(filteredAllPadsMetrics[a]?.createdOn)))
    } else {
      setDisplayPads((prev) => prev.filter((p) => p !== padName)?.sort((a, b) => new Date(filteredAllPadsMetrics[b]?.createdOn) - new Date(filteredAllPadsMetrics[a]?.createdOn)))
    }
  }
};

export default RTFOMapWithWellbore;

function getMetricValue(stageName, metric, stressShadow, stageData, settings, perfSchedule, fracLengthDisplay, stages, monitorEvents=[], fiberData=[]) {
  try {
    const metricParamName = getMetricParamName(metric);
    if (metric === 'Stress Shadow') {
      return stressShadow?.[stageName] || 0;
    } else if (metric === 'Stage Sequence') {
      return stages?.indexOf(stageName) >= 0 ? stages?.indexOf(stageName) + 1 : 0;
    } else if (metric === 'Rate Drop Events') {
      return (stageData?.[stageName]?.events?.perfClosure)?.length || stageData?.perfClosure || 0;
    } else if (metric === 'Plug Slip Events') {
      return (stageData?.[stageName]?.events?.plugSlip || stageData?.[stageName]?.plugSlips)?.length || stageData?.plugSlip || 0;
    } else if (metric === 'Sandoff Events') {
      return stageData?.[stageName]?.events?.sandoff?.length || stageData?.sandoff ||0;
    } else if (metric === 'Isolation Issue Events') {
      return (stageData?.[stageName]?.events?.isolationIssue?.length || stageData?.isolationIssue || 0) + (stageData?.[stageName]?.pumpdownParams?.postPerfISIP - stageData?.[stageName]?.pumpdownParams?.pumpdownISIP < 1 ? 1 : 0);
    } else if (metric === 'Equipment Issue Events') {
      return stageData?.[stageName]?.events?.equipmentIssue?.length || stageData?.equipmentIssue ||0;
    } else if (metric === 'Lost Suction Events') {
      return stageData?.[stageName]?.events?.lostSuction?.length || stageData?.lostSuction ||0;
    } else if (metric === 'Other Events') {
      return stageData?.[stageName]?.events?.other?.length || stageData?.other ||0;
    } else if (metric === 'Fracfeed Events') {
      return stageData?.[stageName]?.events?.fracfeed?.length || stageData?.fracfeed ||0;
    } else if (metric === 'A*sqrt(k)') {
      return stageData?.[stageName]?.fracModel?.aSqrtK || stageData?.aSqrtK ||0;
    } else if (metric === 'Frac Area') {
      return stageData?.[stageName]?.fracModel?.fracArea || stageData?.fracArea ||0;
    } else if (metric === 'Half Length') {
      const fracLength = ((stageData?.[stageName]?.fracModel?.halfLength * 2) || (stageData?.halfLength * 2) || (stageData?.[stageName]?.fracModel?.fracArea / (settings?.fracHeight)) || 600) * fracLengthDisplay;
      return fracLength;
    } else if (metric === 'Total Proppant'){
      return (stageData?.[stageName]?.metrics?.tpmCalc * stageData?.[stageName]?.metrics?.stageLength) || (stageData?.tpmCalc * stageData?.stageLength) || 0;
    } else if (metric === 'Max Proppant'){
      return (stageData?.[stageName]?.metrics?.maxProppant) || 0;
    } else if (metric === 'Total DFR'){
      return (stageData?.[stageName]?.metrics?.frPerFluid * stageData?.[stageName]?.metrics?.totalCleanVolume) || (stageData?.frPerFluid * stageData?.totalCleanVolume) || 0;
    } else if (metric === 'Total LFR'){
      return (stageData?.[stageName]?.metrics?.frPerFluid2 * stageData?.[stageName]?.metrics?.totalCleanVolume) || (stageData?.frPerFluid2 * stageData?.totalCleanVolume) || 0;
    } else if (metric === 'TPM Planned') {
      return perfSchedule?.wells?.[stageName?.split('_')?.[1]?.toLowerCase()]?.stages?.[stageName?.split('_')?.[2]?.replace(/[^0-9]+/g, '')]?.tpm || 0;
    } else if (metric === 'Seismicity Risk') {
      return perfSchedule?.wells?.[stageName?.split('_')?.[1]?.toLowerCase()]?.stages?.[stageName?.split('_')?.[2]?.replace(/[^0-9]+/g, '')]?.seismicRisk || 0;
    } else if (metric === 'Xf') {

        // let diffusivities = [];
        // monitorEvents?.forEach(e => (e.diffusivity > 0 && e.name !== 'Stage Start' && diffusivities.push(e.diffusivity)));
        // diffusivities = diffusivities?.sort((a, b) => a - b);

        // const midPoint = Math.floor(diffusivities?.length / 2);

        // const avgDiffusivitiesLow = diffusivities.slice(0, midPoint);
        // const avgLow = avgDiffusivitiesLow?.reduce((tot, curr) => tot + curr, 0) / avgDiffusivitiesLow?.length;
        // const avgHigh = Math.max(...diffusivities);

        let stageEvents = [];
        stageEvents = stageEvents.concat(monitorEvents?.filter(e => e.stageName === stageName && e.name !== 'Stage Start'));
        stageEvents = stageEvents.concat(fiberData?.filter(e => e.stageName === stageName && e.name !== 'Stage Start'));
        const totalSlurryVolume = stageData?.[stageName]?.metrics?.totalSlurryVolume || stageData?.totalSlurryVolume || 0;

        const xfs = stageEvents?.map(e => {
          // const xfLow = Math.sqrt(4 * Math.PI * totalSlurryVolume * avgLow);
          // const xfHigh = Math.sqrt(4 * Math.PI * totalSlurryVolume * avgHigh);
          // let xf = ((xfLow + xfHigh) / 2) || 0;
          const calculatedXf = Math.sqrt(4 * Math.PI * totalSlurryVolume * e.diffusivity);
          const xf = calculatedXf || 0;
          // const xf = (((e?.xfLow + e?.xfHigh) / 2) || calculatedXf || 0)
          return xf;
        });

        const averageXf = (xfs?.reduce((tot, curr) => tot + curr, 0) / xfs?.length) || 0;
        return +(averageXf?.toFixed(1)) || 0;
    } else if (metric === 'VFR') {
        const stageEvents = monitorEvents?.filter(e => e.stageName === stageName && e.name !== 'Stage Start');
        const vfrs = stageEvents?.map(e => e.vfr || e.slurryVolume);
        const averageVFR = (vfrs?.reduce((tot, curr) => tot + curr, 0) / vfrs?.length) || 0;
        return +(averageVFR?.toFixed(1)) || 0;
    } else if (metric === 'Percent Energy Lost') {
      return +((stageData?.[stageName]?.fracEnergy?.percentEnergyLost * 100)?.toFixed(2)) || 0;
    } else if (metric === 'dP Per Log Cycle') {
      return +(stageData?.[stageName]?.fracModel?.dPPerLogCycle?.toFixed(3)) || 0;
    } else {
      return stageData?.[stageName]?.metrics?.[metricParamName] || stageData?.[metricParamName] || 0;
    };

  } catch (error) {
    console.log(error);
    return 0;
  };
};

export function surveyToCoordinates(surfaceLat, surfaceLon, surveyData) {
  try {
    let coordinates = [];
  
    // Initialize variables for minimum curvature algorithm
    let x0 = surfaceLat;
    let y0 = surfaceLon;
    let z0 = 0;
    let mdPrev = 0;
  
    // Iterate through survey points to calculate wellbore path
    for (let i = 0; i < surveyData.length; i++) {
      let md = surveyData[i].measuredDepth;
      let dip = surveyData[i].deviation;
      let azi = surveyData[i].direction;
      let dMD = md - mdPrev;
      let radians = dip * Math.PI / 180;
      let aziRad = azi * Math.PI / 180;
  
      // Calculate new coordinates with minimum curvature algorithm
      let dx = dMD * Math.sin(radians) * Math.cos(aziRad);
      let dy = dMD * Math.sin(radians) * Math.sin(aziRad);
      let dz = dMD * Math.cos(radians);
      let x = x0 + dx / 111111;
      let y = y0 + dy / (111111 * Math.cos(surfaceLat * Math.PI / 180));
      let z = z0 + dz;
      let lat = x;
      let lon = y;
  
      // Add the lat, lon coordinates to the array
      coordinates.push({lat, lon, tvd: surveyData[i].trueVerticalDepth});
  
      // Update variables for next iteration
      x0 = x;
      y0 = y;
      z0 = z;
      mdPrev = md;
  
    };
  
    return coordinates;
    
  } catch (error) {
    console.log(error);
    return [];
  };
};

export function interpolateCoordinates(surveyData, coordinates, desiredMd) {
  try {
    // find index of the surveyData that is closest to the desiredMd
    let index = surveyData?.findIndex((data) => data.measuredDepth >= desiredMd);

    if (!index) return [0,0];
    
    if (!index > 0 || index === -1) {
      index = surveyData.length - 1;
    };
    const prevIndex = index - 1;
  
    // interpolate coordinates [lat, lon] between index and prevIndex
    const md1 = surveyData[prevIndex].measuredDepth;
    const md2 = surveyData[index].measuredDepth;
    const lat1 = coordinates[prevIndex][0];
    const lat2 = coordinates[index][0];
    const lon1 = coordinates[prevIndex][1];
    const lon2 = coordinates[index][1];
    const lat = ((desiredMd - md1) / (md2 - md1)) * (lat2 - lat1) + lat1;
    const lon = ((desiredMd - md1) / (md2 - md1)) * (lon2 - lon1) + lon1;
  
    return [lat, lon];
    
  } catch (error) {
    console.log(error);
    return [0, 0];
  };

};

export function calculateLineCoordinates(center, length) {
  try {
    // Define the center point and length in meters
    const centerPoint = turf.point([center?.[1], center[0]]); // center point

    // Calculate the start and end points using Turf.js
    const bearing = 90; // Angle in degrees (e.g., 90 for an eastward line)
    const start = turf.destination(centerPoint, length / 2, bearing, { units: 'meters' });
    const end = turf.destination(centerPoint, length / 2, bearing - 180, { units: 'meters' });

    // Extract the coordinates of the start and end points
    const startCoordinates = start.geometry.coordinates;
    const endCoordinates = end.geometry.coordinates;

    return [[startCoordinates?.[1], startCoordinates?.[0]], center, [endCoordinates?.[1], endCoordinates?.[0]]];
  } catch (error) {
    console.log('Calculate line coordinates error');
    return null;
  };
};

export function rotatePolyline(originalLine, angle) {
  try {
    const start = [originalLine?.[0]?.[1], originalLine?.[0]?.[0]];
    const center = [originalLine?.[1]?.[1], originalLine?.[1]?.[0]];
    const end = [originalLine?.[2]?.[1], originalLine?.[2]?.[0]];

    const polyline = turf.lineString([start, center, end]);
    const options = { pivot: center};
    const rotatedPolyline = turf.transformRotate(polyline, -(90-angle), options);

    const rotatedCoordinates = turf.getCoords(rotatedPolyline);

    return [[rotatedCoordinates?.[0]?.[1], rotatedCoordinates?.[0]?.[0]], [rotatedCoordinates?.[1]?.[1], rotatedCoordinates?.[1]?.[0]], [rotatedCoordinates?.[2]?.[1], rotatedCoordinates?.[2]?.[0]]];

  } catch(error) {
    console.log('Rotate line coordinates error');
    return null;
  };
};

export function findTVD(surveyData, md) {
  try {
    
    const closestPoint = surveyData?.find((data) => data.measuredDepth >= md);
    return closestPoint?.trueVerticalDepth || 0;

  } catch (error) {
    console.log(error);
  };
};

// Function to calculate distance between two points on a sphere

export function calculateDistance(start, end) {
  try {
    if (!start || !end || start.length !== 2 || end.length !== 2) return 0;
    const R = 6371e3; // Earth's radius in meters
    const phi1 = (start[0] * Math.PI) / 180; // Convert latitude 1 to radians
    const phi2 = (end[0] * Math.PI) / 180; // Convert latitude 2 to radians
    const deltaPhi = ((end[0] - start[0]) * Math.PI) / 180; // Difference in latitudes
    const deltaLambda = ((end[1] - start[1]) * Math.PI) / 180; // Difference in longitudes
    const a =
      Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
      Math.cos(phi1) *
        Math.cos(phi2) *
        Math.sin(deltaLambda / 2) *
        Math.sin(deltaLambda / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)); // Central angle
    const distance = R * c; // Distance between the two points
    return distance;
    
  } catch (error) {
    console.log(error);
    return 0;
  };
};

export function calculateSignedDistance(start, end) {
  try {
    if (!start || !end || start.length !== 2 || end.length !== 2) return 0;
    const R = 6371e3; // Earth's radius in meters
    const phi1 = (start[0] * Math.PI) / 180; // Convert latitude 1 to radians
    const phi2 = (end[0] * Math.PI) / 180; // Convert latitude 2 to radians
    const deltaPhi = ((end[0] - start[0]) * Math.PI) / 180; // Difference in latitudes
    const deltaLambda = ((end[1] - start[1]) * Math.PI) / 180; // Difference in longitudes
    const a =
      Math.sin(deltaPhi / 2) * Math.sin(deltaPhi / 2) +
      Math.cos(phi1) *
        Math.cos(phi2) *
        Math.sin(deltaLambda / 2) *
        Math.sin(deltaLambda / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)); // Central angle
    const distance = R * c; // Distance between the two points

    // Calculate the initial bearing from start to end
    const y = Math.sin(deltaLambda) * Math.cos(phi2);
    const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(deltaLambda);
    const initialBearing = Math.atan2(y, x);

    // Calculate the signed distance based on the initial bearing
    const signedDistance = Math.sin(initialBearing) * distance;

    return signedDistance;

  } catch (error) {
    console.log(error);
    return 0;
  };
};

export function calculateDistance3D(point1, point2) {
  const horizontalDistance = haversineDistance(point1, point2);
  const verticalDistance = Math.abs(point2.tvd - point1.tvd);
  const distance3D = Math.sqrt(horizontalDistance ** 2 + verticalDistance ** 2);
  return distance3D;

  function haversineDistance(point1, point2) {
    const R = 6371000; // Earth's radius in meters
  
    const lat1 = (Math.PI / 180) * point1.lat;
    const lon1 = (Math.PI / 180) * point1.lon;
    const lat2 = (Math.PI / 180) * point2.lat;
    const lon2 = (Math.PI / 180) * point2.lon;
  
    const dLat = lat2 - lat1;
    const dLon = lon2 - lon1;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) ** 2;
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
    return R * c;
  };
};

export function getMiddlePoint(lines) {
  try {
    let totalLat = 0;
    let totalLon = 0;
    let totalPoints = 0;

    for (const line of lines) {
      let lineLat = 0;
      let lineLon = 0;
      const numPoints = line?.length;
  
      for (const point of line) {
        lineLat += point[0];
        lineLon += point[1];
      };
  
      totalLat += lineLat / numPoints;
      totalLon += lineLon / numPoints;
      totalPoints++;
    };
  
    const avgLat = totalLat / totalPoints;
    const avgLon = totalLon / totalPoints;
  
    return [avgLat, avgLon];
    
  } catch (error) {
    console.log(error);
    return [0, 0];
  };
};

export function calculateStressShadow(stageData, allStages, ss, stageCoordinates, clusterCoordinates, currentStageName, perfSchedule, settings, formation) {
  try {

    const shadowRadius = settings?.shadowRadius || 300; // meters
    const youngModulus = settings?.youngModulus || 3046000; // psi
    const fracWidth = settings?.fracWidth || 0.25; // inches
    const t = settings?.t || 1.2; // exponential decay constant
    
    let newSS = JSON.parse(JSON.stringify(ss));

    const currentStageStartTimestamp = stageData?.[currentStageName]?.stageStartTimestamp;
    const stageNames = allStages?.filter(stageName => stageData?.[stageName]?.stageStartTimestamp >= currentStageStartTimestamp);

    const stagesWithinRadius = {};

    if (!clusterCoordinates?.[currentStageName]) return ss;

    const currentStageClusters = Object.keys(clusterCoordinates?.[currentStageName]);
    const currentFirstClusterCoords = clusterCoordinates?.[currentStageName]?.[currentStageClusters?.[0]]; // coordinates of the first stage cluster
    const currentLastClusterCoords = clusterCoordinates?.[currentStageName]?.[currentStageClusters?.[currentStageClusters?.length - 1]]; // coordinates of the last stage cluster


    // find all stages within shadow radius of the current stage
    for (let stageName of stageNames) {

      if (!clusterCoordinates?.[stageName]) continue;

      const nextStageCenterCoordinates = stageCoordinates?.[stageName]?.center?.center;

      
      for (const currentClusterCoords of [currentFirstClusterCoords, currentLastClusterCoords]) {
        // Calculate lateral (2D) distance from the frac plane to the stage origin point
        const pt = turf.point([nextStageCenterCoordinates?.lon, nextStageCenterCoordinates?.lat]);
        const line = turf.lineString([[currentClusterCoords?.top?.start?.lon, currentClusterCoords?.top?.start?.lat],[currentClusterCoords?.top?.end?.lon, currentClusterCoords?.top?.end?.lat]]);
        const lateralDistance = turf.pointToLineDistance(pt, line, {units: 'meters'});
  
        // Calculate the vertical distance from the frac plane to the stage origin point
        const nextStageCenterDepth = nextStageCenterCoordinates?.tvd;
        const currentFirstClusterTopDepth = currentClusterCoords?.top?.center?.tvd;
        const currentFirstClusterBottomDepth = currentClusterCoords?.bottom?.center?.tvd;
        let verticalDistance = 0;
        if (nextStageCenterDepth > currentFirstClusterTopDepth) { // if frac plane is below the stage origin point
          verticalDistance = nextStageCenterDepth - currentFirstClusterTopDepth;
        } else if (nextStageCenterDepth < currentFirstClusterBottomDepth) { // if frac plane is above the stage origin point
          verticalDistance = currentFirstClusterBottomDepth - nextStageCenterDepth;
        };
  
        const distance = Math.sqrt(lateralDistance ** 2 + verticalDistance ** 2);
  
        if (distance <= shadowRadius && (!stagesWithinRadius[stageName] || distance < stagesWithinRadius[stageName])) {
          stagesWithinRadius[stageName] = distance;
        };
      };

    };
    
    // use cluster spacing as the minimum distance
    let clusterSpacing = stageData?.[currentStageName]?.metrics?.clusterSpacing;
    if (!clusterSpacing > 0) {
      clusterSpacing = formation === 'Montney' ? 20 : formation === 'Duvernay' ? 12 : 35;
    };

    const minDistance = clusterSpacing;

    Object.keys(stagesWithinRadius)?.forEach(stageName => {
      const intrinsicStress = Math.max((fracWidth * youngModulus) / (12 * ((Math.max(stagesWithinRadius[stageName], minDistance) * 3.28084) ** t)) * 0.00689476, 0); // current stage intrinsic stress shadow
      const accumulatedStress = (newSS?.[currentStageName] || 0) /  ((Math.max(stagesWithinRadius[stageName], minDistance) * 3.28084) ** t); // current stage previous accumulated stress decayed by the distance
      const totalStress = (newSS?.[stageName] || 0) + intrinsicStress + accumulatedStress; // add previous stress shadow to the value

      newSS[stageName] = totalStress;
    });

    return newSS;

  } catch (error) {
    console.log(error);
  };
};

export function getSeismicEventColor(magnitude) {
  if (magnitude < 1) {
    return 'lightgreen';
  } else if (magnitude < 2) {
    return 'darkgreen';
  } else if (magnitude < 3) {
    return 'gold';
  } else if (magnitude < 4) {
    return 'orange';
  } else if (magnitude >= 4) {
    return 'red';
  } else {
    return 'lightgrey';
  };
};

function getSeismicEventSize(magnitude) {
  const sizeScalingFactor = 3;
  return (5 + Math.abs(magnitude) * sizeScalingFactor) || 0;
}

export function WellLabel({ center, wellName, currentWellName, wellAlias='' }) {
  try {
    const radius = 1;
    const options = {steps: 16, units: 'kilometers'};
    const circle = turf.circle(center, radius, options);
    const coords = circle?.geometry?.['coordinates']?.[0]?.map(c => [c?.[1], c?.[0]]);

    return (
      <SVGOverlay
        bounds={L.latLngBounds(coords)}
        attributes={{}}
      > 
        <text x={'50%'} y={'50%'} fontSize="16px" textAnchor="start" dominantBaseline="middle" strokeWidth="2px" paintOrder="stroke" stroke="white" fill={wellName === currentWellName ? "red" : "black"} >
          {wellName?.charAt(0)?.toUpperCase()?.concat(wellName?.substring(1, ))} {wellAlias?.length > 0 ? `(${wellAlias})` : ""}
        </text>
      </SVGOverlay>
    );

  } catch (error) {
    return <></>
  };
  
};

export function PadLabel({ center, padName }) {
  try {
    const radius = 1
    const options = { steps: 16, units: 'kilometers' }
    const circle = turf.circle(center, radius, options)
    const coords = circle?.geometry?.['coordinates']?.[0]?.map((c) => [
      c?.[1],
      c?.[0],
    ])

    return (
      <SVGOverlay
        bounds={L.latLngBounds(coords)}
        attributes={{}}
      >
        <text
          x={'50%'}
          y={'50%'}
          fontSize="16px"
          textAnchor="start"
          dominantBaseline="middle"
          strokeWidth="2px"
          paintOrder="stroke"
          stroke="white"
          fill={'black'}
        >
          {padName}
        </text>
      </SVGOverlay>
    )
  } catch (error) {
    return <></>
  }
}

function WellboreCrossSection({ currentStageName, wellCoordinates, stageCoordinates3D, clusterCoordinates3D, stageData, stressShadow, settings, surveyData, fracLengthDisplay, formation, client, darkMode }) {

  const canvasRef = useRef(null);

  
  useEffect(() => {
    try {
      if (!canvasRef.current || !currentStageName) return; // Ensure canvasRef is not null
  
      // find the intersection points from the current stage to all other wellbores
      const stageLatLon = [stageCoordinates3D?.[currentStageName]?.center?.center?.lat, stageCoordinates3D?.[currentStageName]?.center?.center?.lon];
      let stageLineCoordinates = calculateLineCoordinates(stageLatLon, 5000);
      stageLineCoordinates = rotatePolyline(stageLineCoordinates, (settings?.fracAngle || 45));
      // const stageLineCoordinates = rotateLine(getLineCoordinates(stageLatLon, 5000), (settings?.fracAngle || 45)); // use 5 km line to find intersections
  
      const intersectedWellsCoordinates = [];
  
      for (const wellName in wellCoordinates) {
        const intersectionCoordinates = findIntersectionCoordinates(stageLineCoordinates, wellName, wellCoordinates, surveyData);

        if (!intersectionCoordinates) continue;
  
        const intersectedWellCoordinates = findClosestPointToIntersection(wellName, intersectionCoordinates, wellCoordinates);
        const closestStageName = wellName?.toLowerCase() === currentStageName?.split('_')?.[1]?.toLowerCase() ? currentStageName : findClosestStageToIntersection(wellName, intersectionCoordinates, clusterCoordinates3D, currentStageName, stageData, settings?.shadowRadius);
        if (closestStageName) {
          intersectedWellCoordinates.stageName = closestStageName;
        };

        intersectedWellsCoordinates.push(intersectedWellCoordinates);
      };

      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
  
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
  
      // Draw background
      ctx.fillStyle = darkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)';
      ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  
      const centerPoint = calculateCenterPoint(intersectedWellsCoordinates);
  
      if (!centerPoint) return;
  
      // Calculate the center of the canvas
      const centerX = canvasWidth / 2;
      const centerY = canvasHeight / 2;
  
      // Length of the 'X' lines (adjust as needed)
      const lineLength = 5;
  
      // Set the line color and width
      ctx.strokeStyle = 'red'; // You can choose your desired line color
      ctx.lineWidth = 2; // You can adjust the line width as needed
  
      // Draw the 'X' lines at the center point
      ctx.beginPath();
      ctx.moveTo(centerX - lineLength, centerY - lineLength);
      ctx.lineTo(centerX + lineLength, centerY + lineLength);
      ctx.moveTo(centerX + lineLength, centerY - lineLength);
      ctx.lineTo(centerX - lineLength, centerY + lineLength);
      ctx.stroke();
  

      // Calculate the maximum frac length
      const maxFracLength = stageData ? Math.max(...Object.keys(stageData)?.map(stageName => (stageData?.[stageName]?.fracModel?.halfLength || (stageData?.[stageName]?.fracModel?.fracArea / settings?.fracHeight) || 600))) : 600;

      const metersToPixelsX = 1 / (maxFracLength / canvasWidth); // You may need to adjust this based on your canvas scale
      const metersToPixelsY = 2; // You may need to adjust this based on your canvas scale
  
      // Calculate local coordinates for the wellbores
      const wellboreLocalCoordinates = [];

      for (const wellbore of intersectedWellsCoordinates) {
        const { lat, lon, tvd, name, stageName } = wellbore;
  
        // calculate the distance between the wellbore and the center point
        const distanceX = (calculateSignedDistance([centerPoint.lat, centerPoint.lon], [lat, lon]));
        const distanceY = (centerPoint.tvd - tvd);
  
        // Calculate scaled positions for lon and tvd
        const x = centerX + distanceX * metersToPixelsX;
        const y = centerY - distanceY * metersToPixelsY;
  
        wellboreLocalCoordinates.push({x, y, name, stageName});
      };
  
      // Determine color bounds for stress shadow
      const values = [];
      for (let stageName in stressShadow) {
        const value = stressShadow[stageName];
        if (value > 0) {
          values.push(value);
        };
      };
  
      const minValue = (Math.min(...values) * 0.95) || 0;
      const maxValue = (Math.max(...values) * 1.05) || 1;
  
      const colorScale = scaleSequential(interpolateTurbo).domain([minValue, maxValue]);
  
      // Draw the frac rectangles for all intersected wellbores
      for (const wellbore of wellboreLocalCoordinates?.sort((a, b) => {
        if (a.stageName === currentStageName) {
            return 1; // Move 'a' to the end
        } else if (b.stageName === currentStageName) {
            return -1; // Move 'b' to the end
        }
        return 0; // No change in order
      })) {
        const { x, y, stageName } = wellbore;
        
        if (!stageName) continue;

        // Set the rectangle's properties (you can customize these)
        ctx.fillStyle = rgbToRgba(colorScale(stressShadow[stageName]), stageName === currentStageName ? 1 : 0.75);
        ctx.strokeStyle = stageName === currentStageName ? 'rgba(0, 0 , 0, 1)' : 'rgba(0, 0, 0, 0.75)'; // Border color
        ctx.lineWidth = 1; // Border width
    
        // Specify the width and height of the rectangle
        const fracLength = (((stageData?.[stageName]?.fracModel?.halfLength * 2) || (stageData[stageName]?.fracModel?.fracArea / settings?.fracHeight) || 600) * fracLengthDisplay) * metersToPixelsX; // Width of the rectangle in meters
        let fracHeight = (settings?.fracHeight || 70); // Height of the rectangle in meters

        // Special case for WCAP Duvernay pads
        if (client === 'wcap' && formation === 'Duvernay') {
          fracHeight = 30;
        };

        fracHeight = fracHeight * metersToPixelsY; // Height of the rectangle in meters
  
        const fracX = x - fracLength / 2;
        const fracY = y - fracHeight / 2;;
  
        // Draw the rectangle
        ctx.fillRect(fracX, fracY, fracLength, fracHeight); // Fill the rectangle
        ctx.strokeRect(fracX, fracY, fracLength, fracHeight); // Draw the rectangle's border
  
      };
  
  
      // Draw the wellbore circles
      for (const wellbore of wellboreLocalCoordinates) {
        const { x, y, name, stageName } = wellbore;
  
        // Draw a circle at the calculated (x, z) position
        ctx.beginPath();
        ctx.arc(x, y, name === currentStageName?.split('_')?.[1] ? 10 : 5, 0, Math.PI * 2);
        ctx.fillStyle = chartColors[0];
        ctx.fill();
  
        // Draw additional information like wellbore name or ID next to the circle
        ctx.font = `${name === currentStageName?.split('_')?.[1] ? '12px' : '10px'} Arial`;
        ctx.fillStyle = '#000';
        ctx.fillText((stageName?.split('_')?.slice(1, )?.join('_') || name), x + 15, y);
      };
  
  
      // Draw the scale at the bottom
      const scaleStartX = 0; // Start of the scale
      const scaleEndX = canvasWidth;   // End of the scale
      const tickSpacingX = canvasWidth / 10; // Tick mark spacing
  
      ctx.strokeStyle = darkMode ? 'white' : 'black'; // You can choose your desired scale color
      ctx.lineWidth = 1; // You can adjust the line width as needed
      ctx.fillStyle = darkMode ? 'white' : 'black'; // You can choose your desired text color
      ctx.font = '12px Arial'; // You can adjust the font size and family as needed
  
      for (let x = scaleStartX; x <= scaleEndX + canvasWidth / 10; x += tickSpacingX) {
        ctx.beginPath();
        ctx.moveTo(x, canvasHeight - 10); // Adjust the tick mark height as needed
        ctx.lineTo(x, canvasHeight);
        ctx.stroke();
  
        // Calculate the corresponding meter value and display it
        const meterValue = (x - canvasWidth / 2) / metersToPixelsX;
        if (x >= scaleStartX + canvasWidth / 10 && x <= scaleEndX - canvasWidth / 10) {
          ctx.fillText(meterValue?.toFixed(0), meterValue === 0 ? x - 5 : x - 10, canvasHeight - 15); // Adjust text position as needed
        };
      };
      ctx.fillText('m', canvasWidth - 15, canvasHeight - 15); // Adjust text position as needed
  
      // Draw the scale on the left
      const scaleStartY = 0; // Start of the scale
      const scaleEndY = canvasHeight; // End of the scale
      const tickSpacingY = canvasHeight / 10; // Tick mark spacing
  
      ctx.strokeStyle = darkMode ? 'white' : 'black'; // You can choose your desired scale color
      ctx.lineWidth = 1; // You can adjust the line width as needed
      ctx.fillStyle = darkMode ? 'white' : 'black'; // You can choose your desired text color
      ctx.font = '12px Arial'; // You can adjust the font size and family as needed
  
      for (let y = scaleStartY; y <= scaleEndY + canvasHeight / 10; y += tickSpacingY) {
        ctx.beginPath();
        ctx.moveTo(10, y); // Adjust the tick mark width as needed
        ctx.lineTo(0, y);
        ctx.stroke();
  
        // Calculate the corresponding meter value and display it
        const meterValue = (y - canvasHeight / 2) / metersToPixelsY; // Adjust metersToPixelsY as needed
        if (y >= scaleStartY + canvasHeight / 10 && y <= scaleEndY - canvasHeight / 10) {
          ctx.fillText(meterValue, 15, y + 5); // Adjust text position as needed
        };
      };
      ctx.fillText('m', 15, 15); // Adjust text position as needed
  
  
      // Draw a black border around the canvas
      ctx.strokeStyle = darkMode ? 'white' : 'black';
      ctx.lineWidth = 2;
      ctx.strokeRect(0, 0, canvasWidth, canvasHeight);
    } catch (error) {
      console.log(error);
      return;
    };
    
  }, [currentStageName, wellCoordinates, stageCoordinates3D, clusterCoordinates3D, surveyData, settings?.fracHeight, settings?.fracAngle, settings?.shadowRadius, stageData, stressShadow, fracLengthDisplay, client, formation, darkMode]);

  return (
    <canvas
      ref={canvasRef}
      width="400"
      height="400"
    />
  );
};

export function findIntersectionCoordinates(stageLine, wellName, wellCoordinates, surveyData) {
  try {

    // Stage line coordinates (latitude and longitude)
    const lat1 = stageLine?.[0]?.[0];
    const lon1 = stageLine?.[0]?.[1];
    const lat2 = stageLine?.[2]?.[0];
    const lon2 = stageLine?.[2]?.[1];

    // Find the horizontal portion of the wellbore
    const horizontalIndex = surveyData?.findIndex(s => s.deviation >= 75);

    if (!horizontalIndex > 0) return null;

    const horizontalWellCoordinates = wellCoordinates[wellName]?.slice(horizontalIndex, );

    // Wellbore line coordinates (latitude and longitude) - a line from first stage to the last stage
    const lat3 = horizontalWellCoordinates?.[0]?.lat;
    const lon3 = horizontalWellCoordinates?.[0]?.lon;
    const lat4 = horizontalWellCoordinates?.[horizontalWellCoordinates?.length - 1]?.lat;
    const lon4 = horizontalWellCoordinates?.[horizontalWellCoordinates?.length - 1]?.lon;

    // Convert latitude and longitude to radians
    const lat1Rad = (lat1 * Math.PI) / 180;
    const lon1Rad = (lon1 * Math.PI) / 180;
    const lat2Rad = (lat2 * Math.PI) / 180;
    const lon2Rad = (lon2 * Math.PI) / 180;
    const lat3Rad = (lat3 * Math.PI) / 180;
    const lon3Rad = (lon3 * Math.PI) / 180;
    const lat4Rad = (lat4 * Math.PI) / 180;
    const lon4Rad = (lon4 * Math.PI) / 180;

    // Calculate the direction vectors
    const dLon1 = lon2Rad - lon1Rad;
    const dLat1 = lat2Rad - lat1Rad;
    const dLon2 = lon4Rad - lon3Rad;
    const dLat2 = lat4Rad - lat3Rad;

    // Calculate the determinant
    const det = dLon1 * dLat2 - dLon2 * dLat1;

    // Check if the lines are parallel (det == 0)
    if (det === 0) {
      return null; // No intersection
    };

    // Calculate the parameters for the point of intersection
    const t1 = ((lon3Rad - lon1Rad) * dLat2 - (lat3Rad - lat1Rad) * dLon2) / det;
    const t2 = ((lon3Rad - lon1Rad) * dLat1 - (lat3Rad - lat1Rad) * dLon1) / det;

    // Check if the intersection point is within the line segments
    if (t1 >= 0 && t1 <= 1 && t2 >= 0 && t2 <= 1) {
      const latIntersection = lat1Rad + t1 * dLat1;
      const lonIntersection = lon1Rad + t1 * dLon1;
      // Convert back to degrees
      const intersection = [latIntersection * (180 / Math.PI), lonIntersection * (180 / Math.PI)];
      return intersection;
    } else {
      return null; // Intersection is outside the line segments
    };
  } catch (error) {
    console.log(error);
    return null;
  };
};


export function calculateCenterPoint(points) {
  try {
    if (points.length < 2) {
      return { lat: points?.[0]?.lat, lon: points?.[0]?.lon, tvd: points?.[0]?.tvd }; // You need at least two points to calculate a midpoint
    };
  
    // Convert latitude and longitude from degrees to radians
    const toRadians = (angle) => (angle * Math.PI) / 180;
    const toDegrees = (radians) => (radians * 180) / Math.PI;
  
    const filteredPoints = points.filter(p => p.lat !== 0 && typeof p.lat === 'number' && !isNaN(p.lat) && p.lon !== 0 && typeof p.lon === 'number' && !isNaN(p.lon));
    const sumLatitudes = filteredPoints?.reduce((sum, point) => sum + toRadians(point.lat), 0);
    const sumLongitudes = filteredPoints?.reduce((sum, point) => sum + toRadians(point.lon), 0);
    const sumTVD = filteredPoints.reduce((sum, point) => sum + point.tvd, 0);
  
    const averageLatitude = sumLatitudes / filteredPoints.length;
    const averageLongitude = sumLongitudes / filteredPoints.length;
    const averageTVD = sumTVD / filteredPoints.length;
  
    const midpointLatitude = toDegrees(averageLatitude);
    const midpointLongitude = toDegrees(averageLongitude);
  
    return { lat: midpointLatitude, lon: midpointLongitude, tvd: averageTVD };

  } catch (error) {
    console.log(error);
    return { lat: 0, lon: 0, tvd: 0};
  };
};

export function findClosestPointToIntersection(wellName, intersection, wellCoordinates) {
  try {
    if (!intersection) return {lat: 0, lon: 0, tvd: 0, name: wellName};

    let closestPointCoordinates = null;
    let closestDistance = Infinity;
  
    for (const point of wellCoordinates[wellName]) {
      const lat = point.lat;
      const lon = point.lon;
  
      const distance = Math.sqrt(
        Math.pow(lat - intersection[0], 2) +
        Math.pow(lon - intersection[1], 2)
      );
  
      if (distance < closestDistance) {
        closestDistance = distance;
        closestPointCoordinates = {lat, lon, tvd: point.tvd, name: wellName};
      };
    };
  
    return closestPointCoordinates;
    
  } catch (error) {
    console.log(error);
    return {lat: 0, lon: 0, tvd: 0, name: wellName};
  };
};

export function findClosestStageToIntersection(wellName, intersection, clusterCoordinates, currentStageName, stageData, shadowRadius) {
  try {
    if (!intersection) return '';

    let closestStageName = null;
    let closestDistance = Infinity;
    const distanceThreshold = shadowRadius || 300;
    
    const currentStageStartTimestamp = stageData?.[currentStageName]?.coldboreData?.[0]?.timestamp;

    for (const stageName in clusterCoordinates) {
      if (stageName?.split('_')?.[1]?.toLowerCase() !== wellName?.toLowerCase() || stageData?.[stageName]?.coldboreData?.[0]?.timestamp > currentStageStartTimestamp) continue;
  
      for (const clusterNumber in clusterCoordinates[stageName]) {
        const point = clusterCoordinates[stageName][clusterNumber].center.center; // Extract lat and lon
        const lat = point.lat;
        const lon = point.lon;
    
        const distance = calculateDistance([lat, lon], intersection);

        if (distance < distanceThreshold && distance < closestDistance) {
          closestDistance = distance;
          closestStageName = stageName;
        };
      };
    };
  
    return closestStageName;
    
  } catch (error) {
    console.log(error);
    return '';
  };
};

export function rgbToRgba(rgb, alpha) {
  try {
    if (!rgb) return '';

    const rgbValues = rgb.match(/\d+/g); // Extract RGB values using regular expression
  
    if (!rgbValues || rgbValues.length !== 3) {
      throw new Error('Invalid RGB color format');
    };
  
    const [r, g, b] = rgbValues.map(Number);
  
    // Ensure the alpha value is between 0 and 1
    alpha = Math.min(1, Math.max(0, alpha));
  
    // Create the RGBA color string
    const rgba = `rgba(${r}, ${g}, ${b}, ${alpha})`;
  
    return rgba;
  } catch (error) {
    console.log(error);
    return rgb;
  };
};
